import apiClient from './_axios';

export interface WhitelistEntry {
   mac: string;
   label: string;
   addedAt: string;
   addedBy: string;
}

/** Customizable-theme config (portalTheme === 'custom'). Mirrors the server
 *  vendoDevice.portalConfig schema. Media URLs are uploaded via the existing
 *  image/file uploader. */
export interface PortalConfig {
   /** Layout preset — the portal page STRUCTURE (frames, buttons, header),
    *  not just colors. 'classic' (default) or 'gaming'. The standard
    *  SmartFi look is signature-only and not selectable. */
   layout: string;
   /** Per-button styling for [Insert Money, Wifi Rates, Voucher] — classic
    *  layout only; gaming keeps its signature neon look. '' = default. */
   buttons?: {
      insertCoin?: { label?: string; color?: string; icon?: string; shape?: string; style?: string };
      wifiRates?: { label?: string; color?: string; icon?: string; shape?: string; style?: string };
      voucher?: { label?: string; color?: string; icon?: string; shape?: string; style?: string };
   } | null;
   /** Base vibe preset (legacy — superseded by `layout` in the UI; kept so
    *  older saved configs keep their colors). */
   vibe: string;
   /** Layout accent color (hex) — '' = layout default. */
   accent?: string;
   background: {
      mode: 'color' | 'image' | 'video';
      color: string;
      /** Pre-made gradient (CSS string); '' = solid color. */
      gradient?: string;
      imageUrl: string;
      /** Looping muted video background — R2-hosted (zero egress). */
      videoUrl: string;
      opacity: number;
   };
   slider: {
      enabled: boolean;
      images: string[];
      height: number;
   };
   audio: {
      countdownMp3: string;
      insertCoinMp3: string;
   };
   effects: string[];
   /** Per-layout config stashes — each layout remembers its OWN
    *  accent/background/slider/audio/effects; switching layouts never
    *  carries settings over. The flat fields above are the ACTIVE
    *  layout's config (the portal renders those). */
   layoutConfigs?: Record<string, PortalLayoutSlice>;
   /** Operator branding: "Powered by <companyName> & SmartFi". SmartFi is
    *  never removable — the name is only ever added in front of it. */
   brand?: { companyName?: string } | null;
}

/** The per-layout slice of PortalConfig that is stashed/restored when
 *  switching layouts. Button styles stay global (each layout reads only
 *  the button fields it uses). */
export interface PortalLayoutSlice {
   accent: string;
   background: PortalConfig['background'];
   slider: PortalConfig['slider'];
   audio: PortalConfig['audio'];
   effects: string[];
}

export interface ApiVendoDevice {
   _id: string;
   name: string;
   deviceId: string;
   deviceSecret: string;
   portalSlug: string;
   status: 'online' | 'offline' | 'error';
   lastHeartbeat: string | null;
   // Server-derived from router.lastRestOkAt + cluster-root inheritance.
   // Drives the RouterPill — independent from `status` (which is NodeMCU
   // heartbeat freshness).
   routerStatus?: 'connected' | 'agent-down' | 'disconnected' | 'unprovisioned';
   /** Server-derived: an update/reboot is in progress (2d). */
   updateState?: 'idle' | 'updating' | 'rebooting';
   /** False on devices the user has previously sold but still sees for
    *  audit-trail purposes. Drives finance-field redaction + the SOLD
    *  badge in the dashboard. */
   isCurrentOwner?: boolean;
   soldTransfer?: {
      toEmail: string;
      acceptedAt: string | null;
      fullCashAmount: number | null;
   };
   router: {
      // v1 hardware allowlist (ARCHITECTURE.md §13C). Drives router-setup
      // script template selection. Per-model VLAN/sub-vendo caps live in
      // server/src/utils/routerModels.ts (server is source of truth).
      model?: 'hex-rb750gr3' | 'rb4011' | 'ccr2004' | 'ccr2116';
      // Customer ISP-uplink port (default 'ether1'). Excluded from owner-port
      // options so the WAN can never be repurposed.
      wanPort?: string;
      // Owner port (base-subnet redesign A2). MAIN vendo only. A LAN port set
      // to plain 192.168.88.0/24 DHCP with NO captive portal — for the
      // operator's own PC. null/absent = no owner port.
      ownerPort?: string | null;
      // VLAN isolation redesign (script v12). MAIN vendo only. When true, the
      // customer LAN runs on its own tagged VLAN (smartfi-vlan<mainVlanId>) so
      // DHCP/portal never leak untagged onto a shared L2 segment. mainVlanId /
      // mainSubnet customize it; portRoles assign access (untagged, direct
      // customers) vs trunk (tagged, downstream AP/OLT/switch) per LAN port.
      lanIsolation?: boolean;
      mainVlanId?: number;
      mainSubnet?: string;
      /** OpenWrt "Customers on a VLAN tag": when the router last confirmed the
       *  main VLAN is built; null = pending since the last save. */
      mainVlanLiveAt?: string | null;
      /** MikroTik Access Point VLAN (A51): 'mt-pending' while a save waits for the router. */
      mainVlanLiveConfig?: string;
      /** OpenWrt: the internet port the agent reports (e.g. 'eth0'). */
      wanDev?: string;
      portRoles?: { port: string; role: 'access' | 'trunk' }[];
      /** OpenWRT board id (e.g. "ruijie,rg-ew1200g-pro-v1.1") — drives the
       *  hardware badge for Lite devices. */
      hwBoard?: string;
      /** OpenWRT main only: which link carries the tagged sub-vendo VLANs
       *  ('' = board default). Choices come from GET /vlans → openwrt.trunks. */
      vlanTrunkIface?: string;
      /** Ethernet interfaces the OpenWRT agent reports it has, e.g. ['eth0','eth1']. */
      netIfaces?: string[];
      /** Registered product type — cloud or hybrid. Set at claim, durable. */
      variant?: 'cloud' | 'hybrid';
      ip?: string;
      username?: string;
      password?: string;
      /** Router firmware/overlay version the agent reports each heartbeat
       *  (e.g. "1.0.12-72661b0"). Mirrors AdminVendoDevice.router. */
      firmwareVersion?: string;
      vpnIp?: string;
      vpnProvisionedAt?: string | null;
      /** Cloud→box inbound reachability (server cron probes :80 over the
       *  tunnel). false while "online" = instant grant/kick push and the
       *  Awaiting tab are silently degraded. null/undefined = never probed. */
      inboundReachable?: boolean | null;
      inboundCheckedAt?: string | null;
      lastRestProbeAt?: string | null;
      lastRestOkAt?: string | null;
      lastRestError?: string | null;
      // Live Lite OTA progress the agent reports while flashing/failing
      // (cleared once the box reaches the armed version).
      otaStatus?: {
         state: string; // downloading | flashing | failed
         version: string;
         attempts: number;
         error: string; // '' | download | sha
         at: string;
      } | null;
      ssid?: string;
      connected?: boolean;
      /** Router agent's last heartbeat (openwrt). Drives the OrangePi
       *  Heartbeat column — its on-board coin reader has no NodeMCU. */
      lastSeenAt?: string | null;
      /** When the operator last saved a WiFi rename — anchor for the
       *  rename-progress banner (utils/renameProgress). */
      ssidRenamedAt?: string | null;
      /** MikroTik Hybrid only: the cloud's own record of what it wrote to the
       *  router. `dirtyAt` = a save changed what the router-served page reads,
       *  `liveAt` = start of the last pass that landed clean, `error` = why the
       *  last pass failed. Drives the "sending to the router… / live" line. */
      hexSync?: {
         at?: string | null;
         dirtyAt?: string | null;
         liveAt?: string | null;
         error?: string | null;
      } | null;
      /** The addresses of THIS ROUTER'S OWN radios, reported by the agent.
       *  Absent on MikroTik and on older agents — and absence must read as
       *  "unknown", never as "the NodeMCU is somewhere wrong". */
      apBssids?: { bssid: string; freq: number }[];
      /** The box's own account of itself, carried on the ordinary internet —
       *  the road that keeps working when the tunnel is dead. Null until the
       *  box runs an image that reports it. Rendered by deviceDiagText.ts. */
      diag?: {
         hsAge?: number;
         inet?: string;
         reboots24h?: number;
         outage?: string;
         events?: string[];
         /** KB free in the writable area. 🔴 0 MEANS "DID NOT SAY" — it is the
          *  server-side default for every router that predates the field, and
          *  reading it as "no space left" is what cost two days on 5D ALCARDE.
          *  Only a positive number is a reading. */
         freeKb?: number | null;
         /** What the router TRIED about a fault: `<epoch> action=<name>
          *  result=ok|fail`. On the router's flash, so these survive the
          *  reboot that ends the outage. */
         actions?: string[];
         /** Another router's DHCP on the LAN — see deviceDiagText.rogueDhcpLine. */
         dhcpGuard?: string;
         dhcpRogue?: { mac: string; port: string }[];
         at?: string | null;
      } | null;
      /** What the router says about its own condition — the System Info face of
       *  the device card. Null on a router that has never reported it.
       *
       *  🔴 EVERY FIELD IS NULLABLE AND null MEANS "THIS ROUTER DID NOT SAY".
       *  Not zero. Around 48 routers in the field report none of this, and they
       *  must read as silent rather than as a machine pinned at 0%. */
      sys?: {
         /** 1-minute load average × 100 — 47 means a load of 0.47. Never a
          *  percentage on its own: 0.47 across 4 CPUs is about 12% busy. */
         loadCenti?: number | null;
         cores?: number | null;
         memTotalKb?: number | null;
         /** MemAvailable, never MemFree — the board reported 39MB free against
          *  29MB available, and free is the flattering, wrong one. */
         memAvailKb?: number | null;
         cfgFreeKb?: number | null;
         cfgTotalKb?: number | null;
         dlFreeKb?: number | null;
         dlTotalKb?: number | null;
         clients?: number | null;
         at?: string | null;
      } | null;
      /** How a Lite box reaches the internet, when the operator has set it
       *  rather than leaving the image default. OpenWRT only
       *  (Ruijie, OrangePi) — a MikroTik never carries this.
       *
       *  `proto` is what the box reports it is RUNNING; `target` is what it was
       *  last asked to run. They differ while a change is being tested, and
       *  stay different if the box rejected the change and rolled back — only
       *  the box can honestly report that, since a failed change takes out the
       *  tunnel the server would have used to check.
       *
       *  The ISP password is never included. `hasPassword` says whether one is
       *  saved; read the value itself with getDeviceWanPassword(). */
      wan?: {
         proto?: 'dhcp' | 'static' | 'pppoe' | null;
         target?: 'dhcp' | 'static' | 'pppoe' | null;
         /** The VLAN tag the internet is delivered on; null = untagged. */
         vlanId?: number | null;
         username?: string;
         ipaddr?: string;
         netmask?: string;
         gateway?: string;
         dns?: string[];
         status?: 'idle' | 'applying' | 'confirmed' | 'reverted' | 'failed';
         lastAppliedAt?: string | null;
         lastError?: string;
         hasPassword?: boolean;
      } | null;
      /** The ISP port's traffic as of the router's last check-in. A
       *  MEASUREMENT — kept apart from `wan`, which is the operator's setting.
       *  Up to a minute old, so it answers "is there internet and is anything
       *  using it", not "what is the rate this second". */
      wanTraffic?: {
         rxBytes?: number | null;
         txBytes?: number | null;
         rxBps?: number | null;
         txBps?: number | null;
         at?: string | null;
      } | null;
      /** Operator-set coin/slot GPIO wiring (SBC boards). Null = image
       *  defaults. Pushed to the box via the heartbeat. */
      coinConfig?: {
         gpioLine?: string;
         setLine?: string;
         setOpen?: string;
         setClosed?: string;
         debounceMs?: number | null;
      } | null;
      // VLAN opt-in on the main vendo. Once any sub exists, this is
      // read-only — the setVlanEnabledApi endpoint enforces. LEGACY (kept
      // for back-compat reads); the redesign uses router.vlans[] zones.
      vlanEnabled?: boolean;
      // VLAN assignment on a sub-vendo. Populated only when parent has
      // vlanEnabled=true. LEGACY — redesign subs use top-level vlanRef.
      vlan?: {
         id?: number | null;
         subnet?: string | null;
         port?: string | null;
      };
      backhaul?: 'wired' | 'wireless';
      // VLAN redesign (2026-05-31). platform selects the config generator;
      // vlans[] are reusable VLAN "zones" on a MAIN vendo (each sub attaches
      // to one via its top-level vlanRef). Per-VLAN IPs are derived, not stored.
      platform?: 'mikrotik' | 'openwrt';
      vlans?: { vlanId: number; label: string; subnet: string }[];
   };
   ap?: {
      model?: '' | 'tplink-eap110';
   };
   nodeMcu: {
      mac?: string;
      flashSize?: number;
      firmwareVersion?: string;
      lastFlashedAt?: string | null;
      /** Coin-box liveness — set only by the NodeMCU's own heartbeat. */
      lastHeartbeat?: string | null;
      // Link diagnostics, self-reported every heartbeat. The list endpoint does
      // `find(filter)` with no `.select()`, so these were already on the wire —
      // declaring them here is the whole change. See utils/linkHealth.ts for why
      // none of them can be rendered raw.
      /** dBm, always negative. 0 = wired, NOT "perfect". */
      wifiRssi?: number;
      wifiChannel?: number;
      /** ⚠️ RAM counter — resets to 0 every reboot. Only a RATE is meaningful. */
      wifiReconnects?: number;
      /** The specific antenna this box attached to. */
      bssid?: string;
      /** Seconds since boot. Required to read wifiReconnects honestly. */
      uptimeSec?: number;
      /** Why it last booted — a power fault reads differently to a network one. */
      resetReason?: string;
      /** The box could not reach its pinned antenna and joined by name instead.
       *  Distinguishes "gave up on its own router" from "sitting on someone
       *  else's by choice" — they look identical without it. */
      pinFellBack?: boolean;
      // VLAN redesign: how this sub-vendo's NodeMCU reaches the router.
      // 'wifi' joins the zone SSID instead of an ethernet port (firmware
      // support deferred — the fields persist for the generator hooks).
      connMode?: 'ethernet' | 'wifi';
      wifiSsid?: string;
      wifiPassword?: string;
   };
   // VLAN redesign: the parent zone's vlanId this sub-vendo attaches to
   // (null on a main vendo or an unassigned sub).
   vlanRef?: number | null;
   // Partner/host devices granted free permanent internet (revenue-share
   // perk). Operator-managed; each is a permanent ip-binding bypass.
   whitelistedMacs?: WhitelistEntry[];
   portalTheme?: 'gaming' | 'midnight' | 'clean' | 'sunset' | 'ocean' | 'custom';
   /** Customizable-theme config. Present once the operator touches the
    *  'custom' theme; persists even if they switch back to a preset. */
   portalConfig?: PortalConfig | null;
   /** Last portal theme/customization save; a hybrid router picks it up on its next heartbeat. */
   portalConfigSavedAt?: string | null;
   settings: {
      ratePerPeso: number;
      defaultDownload: number;
      defaultUpload: number;
      pauseEnabled: boolean;
      /** Will this vendo accept customers arriving with time or Unlimited from
       *  the operator's other vendos? OpenWRT only; absent means ON. */
      roamingEnabled?: boolean;
      autoPauseOnDisconnect: boolean;
      maxSessionTime: number;
      idleTimeout: number;
      /** "Default validity (pause)": how long FROZEN time is kept, for any
       *  rate that doesn't set its own. 0 = kept for ever. Counted as TOTAL
       *  time paused, so reconnecting never resets it. */
      maxPauseDuration: number;
      allowTethering: boolean;
      /** Track F router security (router-wide, main vendo). Off by default. */
      antiDetect?: boolean;
      antiTorrent?: boolean;
      antiWorm?: boolean;
      // Which tabs the captive portal renders. Default 'both'.
      // 'coin' hides the Voucher tab; 'voucher' hides the Coin tab.
      // Pre-migration devices return undefined → portal treats as 'both'.
      portalMode?: 'coin' | 'voucher' | 'both';
      /** Portal extras, honoured in every portalMode: the "Vouchers" plan-list
       *  button and the "Play game" link. Absent = on. */
      showVoucherRates?: boolean;
      showGame?: boolean;
      /** "View Rates": the Wi-Fi rate list + its button on the portal. Absent = on. */
      showRates?: boolean;
   };
   stats: {
      totalCoins: number;
      todayCoins: number;
      totalSessions: number;
      activeSessions: number;
      /** All-time revenue, untouched by Reset Stats. Survives forever. */
      lifetimeCoins: number;
      /** All-time session count, untouched by Reset Stats. */
      lifetimeSessions: number;
      /** All-time GCash payment sum across this device's sessions. */
      gcashRevenue?: number;
      /** All-time printed (over-the-counter) voucher sales: manual codes at their price, when redeemed. */
      voucherRevenue?: number;
      /** Printed voucher sales today (PH day). */
      todayVouchers?: number;
      /** Online sales today (PH day), net — what the operator receives. */
      todayOnline?: number;
   };
   licenseStatus: 'unactivated' | 'trial' | 'active' | 'expired' | 'lifetime';
   trialStartedAt: string | null;
   trialEndsAt: string | null;
   licenseStartedAt: string | null;
   licenseEndsAt: string | null;
   /** When the paid license was last moved onto this device — drives the
    *  24-hour move-cooldown indicator. Null = never moved / fresh activation. */
   licenseMovedAt: string | null;
   /** Owner flagged this device lost/stolen (Phase 6). */
   markedStolen?: boolean;
   /** Why THIS (claimant) device is ownership-locked: 'pending' (awaiting the
    *  real owner's decision) or 'stolen' (owner denied → blocked). */
   ownershipLockReason?: 'pending' | 'stolen' | null;
   /** Hardware claimed away by another account: 'sold' (granted) or 'stolen'
    *  (denied). Drives the "🔌 No router" banner on the original device. */
   hardwareReleasedReason?: 'sold' | 'stolen' | null;
   /** Set when a reflash just finished; the router restarts its REST for 1-2
    *  min. Drives the "⏳ Setting up the router" card banner. */
   reflashFinishingAt?: string | null;
   autoRenewLicense: boolean;
   // Wizard progress. New devices start at 'flashing'; legacy devices
   // (registered before this field existed) default to 'done'. Drives
   // the "Setup in progress" section on the device list.
   setupStatus: 'flashing' | 'configuring-router' | 'connecting-portal' | 'done';
   // Sub-vendo topology. null parent = main vendo; non-null = sub of
   // that parent device. No cap on sub count.
   parent: string | null;
   // Pricing model in effect at the captive portal. 'time' uses the
   // per-device VendoRate rows; 'data' inherits rates from the linked
   // DataPlan (see activeDataPlan). Mutually exclusive per device.
   activeRateMode?: 'time' | 'data';
   activeDataPlan?: string | null;
   createdAt: string;
   updatedAt: string;
}

export interface ListDevicesResponse {
   devices: ApiVendoDevice[];
}

export async function listDevices(): Promise<ListDevicesResponse> {
   const response = await apiClient.get<ListDevicesResponse>('/vendo/devices');
   return response.data;
}

export interface UpdateDevicePatch {
   name?: string;
   portalTheme?: 'gaming' | 'midnight' | 'clean' | 'sunset' | 'ocean' | 'custom';
   /** Customizable-theme config — sent alongside portalTheme: 'custom'. */
   portalConfig?: PortalConfig;
   settings?: Partial<ApiVendoDevice['settings']>;
   /** MAC of the operator's setup machine — baked into the generated
    *  router script as an ip-binding bypass so the operator never
    *  loses internet at the moment the hotspot starts intercepting. */
   operatorBypassMac?: string;
   /** Owner-controlled auto-renew toggle (1 credit at trial/license end). */
   autoRenewLicense?: boolean;
}

export async function updateDevice(
   id: string,
   patch: UpdateDevicePatch
): Promise<{ device: ApiVendoDevice }> {
   const response = await apiClient.patch<{ device: ApiVendoDevice }>(
      `/vendo/devices/${id}`,
      patch
   );
   return response.data;
}

export interface ApiVendoSession {
   _id: string;
   // Server-synthesized whitelist row (id = wl:<deviceId>:<MAC>) — always
   // active, expiresAt mirrors the device license (null = Unlimited).
   isWhitelist?: boolean;
   /** Whitelist rows only: EVERY vendo this phone is Unlimited on. The row is
    *  one per phone, collapsed server-side from the copy each ticked vendo
    *  carries, so the id's own vendo is just the first grant. */
   vendoDevices?: string[];
   vendoCount?: number;
   /** Whitelist rows only: has the phone actually COLLECTED its Unlimited pass?
    *  False means it works at its own vendo and will NOT follow the customer —
    *  the operator cannot tell those apart without being told. */
   carried?: boolean;
   /** Operator's own note on the row ("Juan, room 3"). */
   note?: string;
   device: { mac: string; ip: string; hostname?: string };
   payment: {
      method: 'coin' | 'gcash' | 'voucher';
      amount: number;
      voucherCode?: string | null;
      /** Set when the session is a free-time promo claim. */
      promo?: string | null;
      // Set on data-rate sessions; presence is the signal that the
      // dashboard's Time column should render data usage instead.
      dataPlan?: string | null;
      capMb?: number | null;
   };
   time: {
      purchased: number;
      remaining: number;
      startedAt: string;
      expiresAt: string | null;
      pausedAt?: string | null;
      /** TOTAL time this session has spent paused, in ms, across every pause.
       *  Absent on rows that predate the counter — those read as 0, which is
       *  honest: we never measured their earlier absences. */
      pausedTotalMs?: number;
   };
   bandwidth: { download: number; upload: number; plan?: string };
   status: 'active' | 'expired' | 'paused' | 'kicked';
   vendoDevice: string | null;
   createdAt: string;
   updatedAt: string;
}

export interface DataSessionUsage {
   bytesIn: number;
   bytesOut: number;
   totalUsedBytes: number;
   capBytes: number;
   percentUsed: number;
}

export interface TimeSessionUsage {
   bytesIn: number;
   bytesOut: number;
   totalUsedBytes: number;
   currentDownloadBps: number;
   currentUploadBps: number;
}

export interface ListSessionsResponse {
   sessions: ApiVendoSession[];
   /** view=history: how many finished sessions match, across every page. */
   historyTotal?: number;
   /** view=live: sessions started today (PH), keyed by vendo _id. */
   todayByVendo?: Record<string, number>;
   // Keyed by session._id. Server fills in live router byte counters
   // for active data sessions only; missing keys mean the session is
   // time-rate, expired, or the router was unreachable when polled.
   dataUsageBySessionId?: Record<string, DataSessionUsage>;
   // Keyed by session._id. For active TIME sessions — bytes used and
   // current throughput from the per-session /queue/simple. Missing
   // keys = data-rate session, expired session, or router unreachable.
   timeUsageBySessionId?: Record<string, TimeSessionUsage>;
   // Keyed by session._id. The pause window in force for that session, in
   // minutes — the rate's own, else the device default. null = frozen time is
   // kept for ever. Resolved server-side so the Sessions table shows the same
   // number the expiry sweep acts on.
   pauseWindowBySessionId?: Record<string, number | null>;
   // Keyed by session._id, and only for a session holding MORE than one
   // entry: what went into it (coin, voucher, the operator's Give Internet),
   // in order. One customer = one session, so the row has to say so.
   entriesBySessionId?: Record<string, SessionEntry[]>;
   // The server's own clock at response time. The sessions table counts
   // down from it instead of the viewing device's clock — see
   // utils/serverClock.ts. Optional so an older API just falls back to
   // the previous (uncorrected) behaviour rather than breaking the timer.
   serverNow?: string;
}

/** One entry that put time or data on a session. */
export interface SessionEntry {
   type: 'coin' | 'gcash' | 'online' | 'voucher' | 'operator' | 'admin';
   amount: number;
   minutes: number;
   capMb: number | null;
   at: string;
   vendoDevice: string | null;
   voucherCode: string | null;
   channel: string;
}

/** view=live: active + paused (+ Unlimited) only, plus today's count per
 *  vendo. view=history: one window of finished sessions with their total,
 *  filtered by vendo ids and part of a MAC on the server. */
export interface ListSessionsParams {
   view: 'live' | 'history';
   skip?: number;
   limit?: number;
   vendo?: string;
   mac?: string;
}

export async function listSessions(params?: ListSessionsParams): Promise<ListSessionsResponse> {
   const response = await apiClient.get<ListSessionsResponse>('/vendo/sessions', { params });
   return response.data;
}

export interface KickSessionResponse {
   msg: string;
   session: ApiVendoSession;
}

export async function kickSession(id: string): Promise<KickSessionResponse> {
   const response = await apiClient.delete<KickSessionResponse>(
      `/vendo/sessions/${id}`
   );
   return response.data;
}

export interface SessionControlResponse {
   msg: string;
   session: ApiVendoSession;
}

/** Freeze a running session: the gate shuts, the time left is kept. */
export async function pauseSession(id: string): Promise<SessionControlResponse> {
   const response = await apiClient.patch<SessionControlResponse>(`/vendo/sessions/${id}/pause`);
   return response.data;
}

/** Start a paused session's clock again from the time it kept. */
export async function resumeSession(id: string): Promise<SessionControlResponse> {
   const response = await apiClient.patch<SessionControlResponse>(`/vendo/sessions/${id}/resume`);
   return response.data;
}

export interface ExtendSessionResponse {
   msg: string;
   session: ApiVendoSession;
}

/** Add time to a running session. `minutes` must be a positive integer. */
export async function extendSession(
   id: string,
   minutes: number,
   /** Validity (pause) from now on, minutes; 0 = kept for ever. Absent = unchanged. */
   validityMinutes?: number | null
): Promise<ExtendSessionResponse> {
   const response = await apiClient.patch<ExtendSessionResponse>(
      `/vendo/sessions/${id}/extend`,
      { minutes, ...(validityMinutes != null ? { validityMinutes } : {}) }
   );
   return response.data;
}

/** Add DATA (megabytes) to a running data session — data-rate vendos. */
export async function addSessionData(
   id: string,
   dataMb: number
): Promise<ExtendSessionResponse & { addedMb?: number }> {
   const response = await apiClient.patch(`/vendo/sessions/${id}/extend`, { dataMb });
   return response.data;
}

export interface GrantSessionResponse {
   session: ApiVendoSession;
   granted?: boolean;
   extended?: boolean;
}

/**
 * Operator-grant free time to a device that connected but hasn't paid
 * (the "Awaiting" tab — e.g. a smart TV that can't run the captive flow).
 * `minutes` must be a positive integer.
 */
export async function grantSession(
   deviceId: string,
   mac: string,
   minutes: number,
   ip?: string,
   /** Validity (pause) for this grant, minutes; 0 = kept for ever. Absent = 1 day. */
   validityMinutes?: number | null
): Promise<GrantSessionResponse> {
   const r = await apiClient.post<GrantSessionResponse>(
      `/vendo/devices/${deviceId}/grant-session`,
      { mac, minutes, ...(ip ? { ip } : {}), ...(validityMinutes != null ? { validityMinutes } : {}) }
   );
   return r.data;
}

/** Operator gives DATA (megabytes) to a phone on a data-rate vendo — a
 *  fresh capped session, or more MB on the one it already has. */
export async function grantSessionData(
   deviceId: string,
   mac: string,
   dataMb: number,
   ip?: string,
   validityMinutes?: number | null
): Promise<GrantSessionResponse & { addedMb?: number }> {
   const r = await apiClient.post(
      `/vendo/devices/${deviceId}/grant-session`,
      { mac, dataMb, ...(ip ? { ip } : {}), ...(validityMinutes != null ? { validityMinutes } : {}) }
   );
   return r.data;
}

/** Operator sets how much pause budget a session has LEFT, from now. null = kept for ever. */
export async function setSessionPauseLeftApi(
   id: string,
   minutesLeft: number | null
): Promise<{ validityMinutes: number; unlimited: boolean }> {
   const r = await apiClient.patch(`/vendo/sessions/${id}/pause-left`, { minutesLeft });
   return r.data;
}

export interface WhitelistResponse {
   whitelistedMacs: WhitelistEntry[];
}

/** Grant a MAC free permanent internet (partner/host perk). */
export async function whitelistMac(
   deviceId: string,
   mac: string,
   label?: string
): Promise<WhitelistResponse> {
   const response = await apiClient.post<WhitelistResponse>(
      `/vendo/devices/${deviceId}/whitelist`,
      { mac, label }
   );
   return response.data;
}

/** Per-vendo outcome of a scope save — one entry per vendo touched. */
export interface WhitelistScopeResult {
   deviceId: string;
   name: string;
   action: 'added' | 'removed' | 'unchanged';
   /** False when the vendo's router did not answer. The grant is still saved
    *  and the box picks it up when it returns, so this is "pending", not a
    *  failure — see the server handler for why a fan-out cannot refuse. */
   applied: boolean;
   error?: string;
}

export interface WhitelistScopeResponse {
   mac: string;
   label: string;
   vendoCount: number;
   results: WhitelistScopeResult[];
}

/**
 * Set the FULL list of vendos one Unlimited phone is free on.
 *
 * An empty `deviceIds` revokes it everywhere. Vendos already correct are left
 * untouched server-side, so re-saving an unchanged list costs no router calls.
 */
export async function setWhitelistScope(
   mac: string,
   deviceIds: string[],
   label?: string
): Promise<WhitelistScopeResponse> {
   const response = await apiClient.put<WhitelistScopeResponse>('/vendo/whitelist', {
      mac,
      deviceIds,
      label,
   });
   return response.data;
}

/** Revoke a partner whitelist entry. */
export async function unwhitelistMac(
   deviceId: string,
   mac: string
): Promise<WhitelistResponse> {
   const response = await apiClient.delete<WhitelistResponse>(
      `/vendo/devices/${deviceId}/whitelist/${encodeURIComponent(mac)}`
   );
   return response.data;
}

export interface RouterScriptResponse {
   script: string;
   devMode: boolean;
   /**
    * First paste line: in dev, the smartfi-api user-add. In prod, a
    * /tool fetch that downloads the full setup script to the router.
    * Always short enough to never paste-truncate.
    */
   bootstrap: string;
   /**
    * Second paste line for prod mode: /import file-name=...
    * Null in dev (the LAN dev path doesn't use fetch+import). Wizard
    * renders this as a separate copy block to be pasted AFTER the
    * fetch finishes — chaining via ; was unreliable in Winbox.
    */
   bootstrapImport: string | null;
}

export interface FetchRouterScriptOpts {
   devMode?: boolean;
   devServerIp?: string;
}

/**
 * Wizard step the operator is currently on. Server tracks this so a
 * half-finished setup can be resumed from the device-list "Setup in
 * progress" section.
 */
export type SetupStatus = ApiVendoDevice['setupStatus'];

export async function updateDeviceSetupStatusApi(
   id: string,
   setupStatus: SetupStatus
): Promise<{ device: ApiVendoDevice }> {
   const response = await apiClient.patch<{ device: ApiVendoDevice }>(
      `/vendo/devices/${id}/setup-status`,
      { setupStatus }
   );
   return response.data;
}

/**
 * Flip the main-vendo's VLAN isolation mode. Called by the Add Sub-vendo
 * wizard on first sub creation when the operator answers Yes to the
 * "isolate sub-vendos" question. Server rejects with 409 if any sub
 * already exists for this main vendo.
 */
export async function setVlanEnabledApi(
   id: string,
   vlanEnabled: boolean
): Promise<{ device: ApiVendoDevice }> {
   const response = await apiClient.patch<{ device: ApiVendoDevice }>(
      `/vendo/devices/${id}/vlan-enabled`,
      { vlanEnabled }
   );
   return response.data;
}

/**
 * Set or clear the owner port (base-subnet redesign A2). Pass a LAN port
 * (e.g. 'ether5') to make it a plain DHCP / no-portal port for the operator's
 * own PC, or null to clear it. MAIN vendo only. The server best-effort pushes
 * the new config to the router; `applied` reflects whether that push landed.
 */
export async function setOwnerPortApi(
   id: string,
   ownerPort: string | null
): Promise<{ msg: string; applied: boolean; device: ApiVendoDevice }> {
   const response = await apiClient.patch<{
      msg: string;
      applied: boolean;
      device: ApiVendoDevice;
   }>(`/vendo/devices/${id}/owner-port`, { ownerPort });
   return response.data;
}

/**
 * Set VLAN isolation on the main vendo (script v12). When `lanIsolation` is true
 * the customer LAN moves onto its own tagged VLAN so DHCP/portal never leak
 * untagged onto a shared L2 segment. `mainVlanId` / `mainSubnet` customize it;
 * `portRoles` override the access/trunk split per LAN port. The server
 * re-validates, best-effort pushes the config, and returns the resolved
 * access/trunk split. Turning it ON re-leases active customers onto the VLAN.
 */
export async function setLanIsolationApi(
   id: string,
   body: {
      lanIsolation: boolean;
      mainVlanId?: number;
      mainSubnet?: string;
      portRoles?: { port: string; role: 'access' | 'trunk' }[];
   }
): Promise<{
   msg: string;
   applied: boolean;
   resolvedPorts: { accessPorts: string[]; trunkPorts: string[] };
   /** OpenWrt only: the wiring after the save (which port carries the tag). */
   openwrt?: OpenwrtWiring;
   /** OpenWrt only: the trunk the server picked for the tag on this save, if it did. */
   trunkPicked?: string | null;
   device: ApiVendoDevice;
}> {
   const response = await apiClient.patch<{
      msg: string;
      applied: boolean;
      resolvedPorts: { accessPorts: string[]; trunkPorts: string[] };
      openwrt?: OpenwrtWiring;
      trunkPicked?: string | null;
      device: ApiVendoDevice;
   }>(`/vendo/devices/${id}/lan-isolation`, body);
   return response.data;
}

/**
 * LAN ports an operator can pick as the owner port, per router model. Mirrors
 * lanPorts in server/src/utils/routerModels.ts (server is source of truth and
 * re-validates on write). The WAN port is excluded by the caller.
 */
export const LAN_PORTS_BY_MODEL: Record<string, string[]> = {
   'hex-rb750gr3': ['ether2', 'ether3', 'ether4', 'ether5'],
   rb4011: ['ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'sfp-sfpplus1'],
   ccr2004: ['ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'ether11', 'ether12', 'ether13', 'ether14', 'ether15', 'ether16', 'sfp-sfpplus1', 'sfp-sfpplus2'],
   ccr2116: ['ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'ether11', 'ether12', 'ether13', 'sfp-sfpplus1', 'sfp-sfpplus2', 'sfp-sfpplus3', 'sfp-sfpplus4'],
};

export interface CoinConfigInput {
   gpioLine?: string;
   setLine?: string;
   setOpen?: string;
   setClosed?: string;
   debounceMs?: number | null;
   reset?: boolean;
}

/** Set (or reset) the coin/slot GPIO wiring for an SBC board (OrangePi). Pushed
 *  to the device via the heartbeat → coin.conf rewrite + coinbridge restart. */
export async function setCoinConfigApi(
   id: string,
   input: CoinConfigInput
): Promise<{ msg: string; device: ApiVendoDevice }> {
   const response = await apiClient.patch<{ msg: string; device: ApiVendoDevice }>(
      `/vendo/devices/${id}/coin-config`,
      input
   );
   return response.data;
}

/* ------------------------------------------------------------------ *
 *  VLAN-zone CRUD (VLAN redesign 2026-05-31)
 *  Reusable per-MAIN-vendo VLAN zones (router.vlans[]). A sub attaches to
 *  one via vlanRef. Per-VLAN IPs (.1 gw / .250 NodeMCU) are server-derived.
 * ------------------------------------------------------------------ */

/** A VLAN zone as returned by GET /vendo/devices/:id/vlans — includes the
 *  server-derived IP layout + which sub (if any) is attached. */
export interface VlanZone {
   vlanId: number;
   label: string;
   subnet: string;
   gateway: string;
   poolStart: string;
   poolEnd: string;
   nodeMcuIp: string;
   // Push lifecycle: pending (gray, not yet confirmed on router), live (green,
   // verified on router), failed (red, router won't take it after retries).
   status: 'pending' | 'live' | 'failed';
   pushAttempts: number;
   lastPushAt: string | null;
   attachedSub: { deviceId: string; name: string } | null;
}

/** Wiring choices for an OpenWRT (OrangePi) main — which single link carries
 *  the tagged sub-vendo VLANs. A MikroTik tags every zone on all its ports, so
 *  it has no such choice and the field is absent there. */
export interface OpenwrtWiring {
   board: string | null;
   hwBoard: string | null;
   /** False = this board cannot host sub-vendos yet. */
   supported: boolean;
   /** The link the router will actually use (operator choice or board default). */
   trunkIface: string | null;
   /** The operator's raw choice; '' = none made yet. */
   chosen: string;
   /** Interfaces the router reported. [] = it has not reported yet. */
   netIfaces: string[];
   trunks: {
      iface: string;
      label: string;
      hint: string;
      /** null = the router has not reported its interfaces yet. */
      available: boolean | null;
   }[];
}

export interface ListVlanZonesResponse {
   platform: 'mikrotik' | 'openwrt';
   /** Present only when platform is 'openwrt'. */
   openwrt?: OpenwrtWiring;
   zones: VlanZone[];
   /** True while a send to the router is actually in progress (self-clearing). */
   pushing?: boolean;
   summary: { total: number; live: number; pending: number; failed: number };
}

export async function listVlanZonesApi(deviceId: string): Promise<ListVlanZonesResponse> {
   const response = await apiClient.get<ListVlanZonesResponse>(
      `/vendo/devices/${deviceId}/vlans`
   );
   return response.data;
}

/** Pick which link carries the tagged sub-vendo VLANs on an OpenWRT main.
 *  Saved only — the router reads it on its next check-in. */
export async function setVlanTrunkApi(
   deviceId: string,
   trunkIface: string
): Promise<{ msg: string; openwrt: OpenwrtWiring }> {
   const response = await apiClient.patch<{ msg: string; openwrt: OpenwrtWiring }>(
      `/vendo/devices/${deviceId}/vlan-trunk`,
      { trunkIface }
   );
   return response.data;
}

export interface CreateVlanZonesResponse {
   msg: string;
   created: { vlanId: number; label: string; subnet: string }[];
   skipped: number[];
}

/**
 * Create one or many VLAN zones. Pass `spec` for a single id ("11") or an
 * inclusive range ("11-100") with auto-allocated /24 subnets, OR `vlanId`
 * (+ optional explicit `subnet`/`label`) for a single zone. Idempotent —
 * already-present IDs are skipped.
 */
export async function createVlanZonesApi(
   deviceId: string,
   payload: { spec: string } | { vlanId: number; subnet?: string; label?: string }
): Promise<CreateVlanZonesResponse> {
   const response = await apiClient.post<CreateVlanZonesResponse>(
      `/vendo/devices/${deviceId}/vlans`,
      payload
   );
   return response.data;
}

/** Heal stuck zones: reset failed/stuck zones to pending and re-push. */
export async function retryVlanZonesApi(
   deviceId: string
): Promise<{ msg: string; reset: number }> {
   const response = await apiClient.post<{ msg: string; reset: number }>(
      `/vendo/devices/${deviceId}/vlans/retry`
   );
   return response.data;
}

export async function updateVlanZoneApi(
   deviceId: string,
   vlanId: number,
   payload: { label?: string; subnet?: string }
): Promise<{ msg: string; zone: { vlanId: number; label: string; subnet: string } }> {
   const response = await apiClient.patch<{
      msg: string;
      zone: { vlanId: number; label: string; subnet: string };
   }>(`/vendo/devices/${deviceId}/vlans/${vlanId}`, payload);
   return response.data;
}

export async function deleteVlanZoneApi(
   deviceId: string,
   vlanId: number
): Promise<{ msg: string }> {
   const response = await apiClient.delete<{ msg: string }>(
      `/vendo/devices/${deviceId}/vlans/${vlanId}`
   );
   return response.data;
}

export interface ProvisionBlob {
   firmwareVersion: string;
   baseFirmwareUrl: string;
   identitySector: {
      address: number;
      size: number;
      bytesBase64: string;
   };
   /** Set when the WiFi name about to be written to the chip is not the one
    *  this router broadcasts. Warning only — bridging your own access point is
    *  a valid setup — but it is the last moment it can be caught, because a
    *  wrong name here is invisible afterwards: the board simply never appears. */
   wifiWarning?: string;
}

/**
 * Server-side preparation for a WebSerial flash. Returns the URL of
 * the base firmware .bin (operator's browser fetches directly) and
 * the pre-computed 84-byte identity sector ready to write at
 * 0x3FB000.
 */
export async function getProvisionBlob(id: string): Promise<ProvisionBlob> {
   const response = await apiClient.post<ProvisionBlob>(
      `/vendo/devices/${id}/provision-blob`
   );
   return response.data;
}

export interface DeleteDeviceResponse {
   msg: string;
   deviceId: string;
   /** Populated when the deleted device was a VLAN sub-vendo. Counts the
    *  resources the server removed from the cluster root's router and
    *  any per-resource failures the operator should know about. */
   routerCleanup?: {
      removed: number;
      failed: { resource: string; error: string }[];
   };
   /** Set when the cleanup itself could not run (router unreachable,
    *  auth failed, etc.). Mongo delete still went through. */
   routerCleanupError?: string;
}

/**
 * Permanently remove a device. Server refuses with 409 when the device
 * has live sessions or attached sub-vendos. Surface those messages to
 * the operator — they're actionable.
 */
export async function deleteDeviceApi(
   id: string,
   opts: { cascade?: boolean } = {}
): Promise<DeleteDeviceResponse> {
   // cascade=1 also deletes the sub-vendos attached to a MAIN (the server
   // refuses with 409 + `subs` otherwise). Needed for a main whose setup
   // never finished: its subs are nested under it and never shown on
   // their own, so the operator has no other way to reach them.
   const response = await apiClient.delete<DeleteDeviceResponse>(
      `/vendo/devices/${id}${opts.cascade ? '?cascade=1' : ''}`
   );
   return response.data;
}

/** Sub-vendo named by a 409 "attached sub-vendos" refusal of deleteDeviceApi. */
export interface BlockingSubVendo {
   id: string;
   deviceId: string;
   name: string;
   setupStatus?: string;
}

export interface PushLoginHtmlResponse {
   msg: string;
}

/**
 * Render the captive-portal login.html for this device and have the
 * server PUT it to the router via MikroTik REST. The router script
 * must have already run (smartfi-api user + REST enabled).
 */
export async function pushLoginHtml(
   id: string
): Promise<PushLoginHtmlResponse> {
   const response = await apiClient.post<PushLoginHtmlResponse>(
      `/vendo/devices/${id}/push-login-html`
   );
   return response.data;
}

export interface VerifyRouterConfigResponse {
   ok: boolean;
   checks: {
      smartfiApiAuth: boolean;
      smartfiBridge: boolean;
      lanList: boolean;
      hotspot: boolean;
      hotspotEnabled: boolean;
      walledGarden: boolean;
      smartfiUsersProfile: boolean;
      // The tail of the setup script (§13–§16). A truncated import drops these
      // while everything above still lands, so they must be shown — otherwise
      // the wizard blocks with every visible row green.
      dnsRemoteRequests: boolean;
      ipv6Disabled: boolean;
      operatorLockout: boolean;
   };
   error?: string;
}

export interface ApplyRouterConfigResponse {
   ok: boolean;
   msg?: string;
   error?: string;
   stage?: string;
   /** The apply was started and runs in the background; the device row carries the result. */
   queued?: boolean;
}

export interface ApplyRouterConfigOpts {
   devMode?: boolean;
   devServerIp?: string;
   /**
    * Wait for the whole apply before replying (the registration wizard and the
    * re-flash flow need the result). Off by default: the server replies at once
    * and applies in the background -- on a 100-zone RB4011 the apply takes
    * minutes and the web server cut the old synchronous reply at 90 s, so the
    * sub-vendo wizard said "router push failed" for a push that succeeded.
    */
   wait?: boolean;
}

/**
 * Apply the entire setup script to the router via REST. Used in place
 * of the operator's terminal paste — they paste only the bootstrap
 * /user add line; this endpoint does the rest.
 */
export async function applyRouterConfig(
   id: string,
   opts: ApplyRouterConfigOpts = {}
): Promise<ApplyRouterConfigResponse> {
   const params: Record<string, string> = {};
   if (opts.devMode) {
      params.dev = '1';
      if (opts.devServerIp) params.devServerIp = opts.devServerIp;
   }
   if (opts.wait) params.wait = '1';
   const response = await apiClient.post<ApplyRouterConfigResponse>(
      `/vendo/devices/${id}/apply-router-config`,
      undefined,
      { params }
   );
   return response.data;
}

/**
 * Remotely reboot a main vendo's MikroTik router (2e-B). Server sends
 * `/system/reboot` over the WG tunnel; the box drops all customers for
 * ~1–2 minutes and comes back with paid time preserved.
 */
export async function rebootRouter(
   id: string
): Promise<{ ok: boolean; msg: string; queued?: boolean }> {
   // `queued` marks the Lite path: the command rides the box's heartbeat rather
   // than being fired over the tunnel, so the box may restart in seconds — or
   // whenever it next comes back. The dialog must not promise "1–2 minutes" for
   // a box that is currently unreachable.
   const response = await apiClient.post<{
      ok: boolean;
      msg: string;
      queued?: boolean;
   }>(`/vendo/devices/${id}/reboot`);
   return response.data;
}

/* ── Operator self-service updates (2d) ─────────────────────────────── */

export type DeviceUpdateState = 'idle' | 'updating' | 'rebooting';

export interface DeviceUpdateStatus {
   updateState: DeviceUpdateState;
   platform: 'mikrotik' | 'openwrt';
   routerConfig: {
      appliedVersion: number;
      currentVersion: number;
      stale: boolean;
      /**
       * Config is at the right version but did NOT fully land on the router —
       * the setup import stopped partway. Needs a re-push, not an upgrade.
       * (RouterOS 7.23 dropped everything after §8, which left paying customers
       * unable to browse while the panel showed the device as up to date.)
       */
      needsRepair: boolean;
      lastAppliedOk: boolean;
      lastAppliedError: string | null;
      lastAppliedAt: string | null;
      state: DeviceUpdateState;
   } | null;
   firmware: {
      running: string | null;
      /** The no-reboot update installed on top of the image, if any. */
      runningBundle?: string | null;
      latest: string | null;
      notes: string;
      /** Every released version for this device's own board and product, newest
       *  first — the "View All" list under What's new.
       *
       *  Deliberately NOT the admin firmware list: that one is gated behind
       *  requireTab('firmware') because it carries dev-channel rows, every
       *  board, storage keys and uploader ids. This carries a version, its
       *  upload note and a date, and nothing else. */
      history?: {
         version: string;
         notes: string;
         at?: string | null;
         /** The version this device is on right now. */
         running?: boolean;
         /** The version currently offered to it. */
         offered?: boolean;
      }[];
      stale: boolean;
      otaStatus: { state: string; version: string; error: string } | null;
      /** Scheduled update: when the armed target will be handed to the router
       *  (ISO, UTC). null = not scheduled / immediate. */
      scheduledAt?: string | null;
      /** Standing quiet hour on this owner's routers ("do this for all my
       *  vendos"): every new release installs then, by itself. null = off. */
      autoUpdate?: { hour: number; minute: number; tzOffsetMin: number } | null;
      state: DeviceUpdateState;
      /**
       * True when this router can take the small updates that do NOT reflash
       * it — script, portal and agent changes that cannot damage it, because
       * they never touch the part of the chip that starts the router.
       *
       * Absent on an older server, which is why it is optional; treated as
       * false, so the cautious banner is what shows when we do not know.
       */
      safeUpdates?: boolean;
      /** The safe update waiting for this router, if any. */
      scriptUpdate?: { version: string } | null;
   } | null;
   coinBox: {
      running: string | null;
      latest: string | null;
      notes: string;
      stale: boolean;
      /** Bound to the vendo but has never reported a version — i.e. the flash
       *  was skipped during registration. Neither stale nor up to date. */
      neverFlashed: boolean;
      /** Flashed during registration but has NEVER checked in — it needs
       *  power, not a USB re-flash. Distinct from neverFlashed on purpose:
       *  the two have different next actions. */
      neverPowered?: boolean;
      canOta: boolean;
      /**
       * Why the Update button is missing, when it is missing for a reason the
       * operator can ACT on. 'wired-too-old' = the board is on a cable and its
       * firmware predates 1.0.20, so it cannot update over the air — but one
       * USB flash fixes that permanently. Null when there is nothing to say.
       */
      usbReason?: 'wired-too-old' | null;
      otaOptIn: boolean;
      state: DeviceUpdateState;
      /** The server tried `attempts` times and has STOPPED. Without this a box
       *  that gave up looks identical to one still updating, and the tab says
       *  "Updating…" forever for something that will never happen. */
      gaveUp?: boolean;
      /** Armed, but the board has not been heard from — waiting, not working. */
      waitingForBox?: boolean;
      attempts?: number;
      maxAttempts?: number;
      sentVersion?: string | null;
   } | null;
   /**
    * Who last ran an update on this vendo. Non-null once anyone has. Shown
    * on both sides: the buyer finds out why their vendo restarted without
    * opening a support chat, the seller sees their own last visit.
    * Optional so an older server (which sends neither) still renders.
    */
   lastServicedBy?: {
      email: string;
      at: string;
      action: 'router-config' | 'router-firmware' | 'coin-firmware' | null;
      /** The actor was the SELLER, not the current owner. */
      bySeller: boolean;
   } | null;
   /** asSeller is true only when the CALLER is servicing a vendo they sold. */
   serviceAccess?: { asSeller: boolean; buyerEmail: string };
}

export async function getDeviceUpdateStatus(
   id: string
): Promise<DeviceUpdateStatus> {
   const r = await apiClient.get<DeviceUpdateStatus>(
      `/vendo/devices/${id}/update-status`
   );
   return r.data;
}

/** Arm the Ruijie (Lite) router firmware OTA to the active image. */
export async function updateDeviceFirmware(
   id: string,
   /** Scheduled update (2026-09-09): the operator's chosen moment, ISO. Omit = now. */
   scheduledAt?: string
): Promise<{ ok: boolean; target: string; scheduledAt?: string | null }> {
   const r = await apiClient.post<{ ok: boolean; target: string; scheduledAt?: string | null }>(
      `/vendo/devices/${id}/update-firmware`,
      scheduledAt ? { scheduledAt } : {}
   );
   return r.data;
}

/** Cancel a scheduled (or armed, not yet collected) firmware update. */
export async function cancelDeviceFirmwareUpdate(id: string): Promise<{ ok: boolean }> {
   const r = await apiClient.delete<{ ok: boolean }>(`/vendo/devices/${id}/update-firmware`);
   return r.data;
}

/** "Do this for all my vendos, every time there is a new update": save a
 *  standing quiet hour on every Ruijie/OrangePi I own. */
export async function setAutoUpdateAll(q: {
   hour: number;
   minute: number;
   tzOffsetMin: number;
}): Promise<{ ok: boolean; count: number; matched: number }> {
   const r = await apiClient.put<{ ok: boolean; count: number; matched: number }>(
      '/vendo/auto-update',
      q
   );
   return r.data;
}

/** Turn the standing quiet hour off on all my routers. */
export async function clearAutoUpdateAll(): Promise<{ ok: boolean; count: number }> {
   const r = await apiClient.delete<{ ok: boolean; count: number }>('/vendo/auto-update');
   return r.data;
}

export interface WanConfigPayload {
   proto: 'dhcp' | 'static' | 'pppoe';
   /** 1-4094 = the internet arrives tagged on the port; null = untagged. */
   vlan?: number | null;
   username?: string;
   password?: string;
   ipaddr?: string;
   netmask?: string;
   gateway?: string;
   dns?: string[];
}

/**
 * Change how a Lite box reaches the internet.
 *
 * Resolving does NOT mean the change worked — it means the box accepted it and
 * armed its own rollback. The box then spends up to `verifySeconds` deciding
 * whether the new settings can actually reach the world, and puts the old ones
 * back if they cannot. Watch the device's `router.wan.status` for the verdict;
 * it arrives on a heartbeat, not in this response.
 */
export async function setDeviceWan(
   id: string,
   cfg: WanConfigPayload
): Promise<{ ok: boolean; verifySeconds: number; queued?: boolean }> {
   const r = await apiClient.patch<{ ok: boolean; verifySeconds: number; queued?: boolean }>(
      `/vendo/devices/${id}/wan`,
      cfg
   );
   return r.data;
}

/** Read back the saved ISP password. Deliberately its own request — it is not
 *  bundled into the device list, so it only travels when someone asks. */
export async function getDeviceWanPassword(id: string): Promise<string> {
   const r = await apiClient.get<{ password: string }>(
      `/vendo/devices/${id}/wan/password`
   );
   return r.data.password;
}

/** What is going in and out of the ISP port right now.
 *
 *  `source` matters to what the dashboard is allowed to claim. 'live' is read
 *  off the router this second; 'heartbeat' is the router's last check-in, which
 *  can be a minute old — fine for "there is internet", not fine to draw as a
 *  live rate. Counters are cumulative and reset when the router restarts, which
 *  is what `uptime` is for: a smaller number than last time is a restart, not
 *  negative traffic. */
export interface WanTrafficSample {
   available: boolean;
   source?: 'live' | 'heartbeat';
   reason?: string;
   dev?: string;
   proto?: string;
   rxBytes?: number;
   txBytes?: number;
   /** Only on the heartbeat source — the router's own arithmetic. */
   rxBps?: number | null;
   txBps?: number | null;
   uptime?: number;
   at?: string;
}

export async function getDeviceWanTraffic(id: string): Promise<WanTrafficSample> {
   const r = await apiClient.get<WanTrafficSample>(
      `/vendo/devices/${id}/wan/traffic`
   );
   return r.data;
}

/** Arm/disarm the NodeMCU firmware OTA for this device. */
export async function setNodeMcuOta(
   id: string,
   otaOptIn: boolean
): Promise<{ otaOptIn: boolean }> {
   const r = await apiClient.patch<{ otaOptIn: boolean }>(
      `/vendo/devices/${id}/nodemcu-ota`,
      { otaOptIn }
   );
   return r.data;
}

/**
 * Verify the setup script applied to the router. Used by the wizard's
 * Step 4 to refuse to advance when paste-truncation in the Winbox
 * terminal silently dropped lines.
 */
export async function verifyRouterConfig(
   id: string
): Promise<VerifyRouterConfigResponse> {
   const response = await apiClient.get<VerifyRouterConfigResponse>(
      `/vendo/devices/${id}/verify-router-config`
   );
   return response.data;
}

/**
 * Send the NodeMCU MAC captured by the WebSerial flasher (esptool-js
 * exposes it during chip detection) to the server, so the router script
 * generated at Step 4 includes the ip-binding bypass section. Without
 * this, fresh setups hit a chicken-and-egg trap where the captive
 * portal intercepts the NodeMCU's first heartbeat and the device stays
 * offline.
 */
export async function setNodeMcuMac(deviceId: string, mac: string): Promise<void> {
   await apiClient.patch(`/vendo/devices/${deviceId}/nodemcu-mac`, { mac });
}

/**
 * Save the WiFi the NodeMCU should join (wireless). Blank ssid reverts
 * the device to wired. Takes effect on the chip after a re-flash.
 */
export interface SsidMismatchError {
   error: string;
   code: 'ssid-mismatch';
   routerSsid: string;
   boxSsid: string;
}

/** True when a nodemcu-wifi save was refused because the name does not match
 *  the router's own WiFi. Re-send with allowMismatch to go ahead anyway. */
export function isSsidMismatch(err: unknown): SsidMismatchError | null {
   const data = (err as { response?: { status?: number; data?: SsidMismatchError } })
      ?.response;
   if (data?.status === 409 && data.data?.code === 'ssid-mismatch') return data.data;
   return null;
}

export async function updateNodeMcuWifi(
   deviceId: string,
   payload: { wifiSsid: string; wifiPassword?: string; allowMismatch?: boolean }
): Promise<{ msg: string; connMode: 'wifi' | 'ethernet'; wifiSsid: string }> {
   const res = await apiClient.patch<{ msg: string; connMode: 'wifi' | 'ethernet'; wifiSsid: string }>(
      `/vendo/devices/${deviceId}/nodemcu-wifi`,
      payload
   );
   return res.data;
}

// Operator-selected NodeMCU pins (raw ESP8266 GPIO numbers; null = firmware
// default). Saved on the device → baked into the identity sector on the next flash.
export interface NodeMcuPins {
   coinPulse: number | null;
   coinPower: number | null;
   w5500Cs: number | null;
   relay: number | null;
}
export async function updateNodeMcuPins(
   deviceId: string,
   pins: NodeMcuPins
): Promise<{ msg: string; pins: NodeMcuPins }> {
   const res = await apiClient.patch<{ msg: string; pins: NodeMcuPins }>(
      `/vendo/devices/${deviceId}/nodemcu-pins`,
      pins
   );
   return res.data;
}

export type CoinBoxVerdict =
   | 'not-lite'
   | 'sub-vendo'
   | 'no-board-flashed'
   | 'healthy'
   | 'no-tunnel'
   | 'router-dark'
   | 'router-unreachable'
   | 'board-not-on-wifi'
   | 'board-on-wifi-cannot-reach';

export interface CoinBoxDiagnosis {
   verdict: CoinBoxVerdict;
   title: string;
   detail: string;
   swapBoardCouldHelp: boolean;
   neverReported: boolean;
}

/**
 * Ask the ROUTER whether the coin board is even on its WiFi, instead of
 * guessing from the dashboard. Splits the two faults that look identical here:
 * a board that never joined, and a board that joined but cannot reach us
 * (router/tunnel — no replacement board fixes that one).
 */
export async function getCoinBoxDiagnosis(
   deviceId: string
): Promise<CoinBoxDiagnosis> {
   const res = await apiClient.get<CoinBoxDiagnosis>(
      `/vendo/devices/${deviceId}/coinbox-diagnosis`
   );
   return res.data;
}

export async function fetchRouterScript(
   deviceId: string,
   opts: FetchRouterScriptOpts = {}
): Promise<RouterScriptResponse> {
   const params: Record<string, string> = {};
   if (opts.devMode) {
      params.dev = '1';
      if (opts.devServerIp) params.devServerIp = opts.devServerIp;
   }
   const response = await apiClient.get<RouterScriptResponse>(
      `/vendo/devices/${deviceId}/router-script`,
      { params }
   );
   return response.data;
}

export type WgStatusResponse =
   | { provisioned: false }
   | {
        provisioned: true;
        vpnIp: string;
        vpnPubkey: string;
        latestHandshakeAt: string | null;
        isUp: boolean;
     };

export async function getWgStatus(deviceId: string): Promise<WgStatusResponse> {
   const r = await apiClient.get<WgStatusResponse>(
      `/vendo/devices/${deviceId}/wg-status`
   );
   return r.data;
}

// ─── DHCP leases + ip-binding bypass management ─────────────
// Powers the wizard's "Bypass operator devices" panel — after the
// operator applies config, the hotspot starts intercepting every
// LAN client including the operator's own laptop. The panel reads
// the router's DHCP lease table so the operator can pick which is
// theirs and add it to the bypass list, restoring their internet
// without manually typing MAC addresses in Winbox.

export interface DhcpLease {
   id: string;
   mac: string;
   address: string;
   hostname: string;
   status: string;
   dynamic: boolean;
}

export interface IpBindingEntry {
   id: string;
   mac: string;
   type: string;
   comment: string;
}

export async function listDhcpLeases(deviceId: string): Promise<{ leases: DhcpLease[] }> {
   const r = await apiClient.get<{ leases: DhcpLease[] }>(
      `/vendo/devices/${deviceId}/dhcp-leases`
   );
   return r.data;
}

export async function listIpBindings(deviceId: string): Promise<{ bindings: IpBindingEntry[] }> {
   const r = await apiClient.get<{ bindings: IpBindingEntry[] }>(
      `/vendo/devices/${deviceId}/ip-bindings`
   );
   return r.data;
}

export async function addIpBinding(
   deviceId: string,
   mac: string,
   comment: string
): Promise<{ added: boolean }> {
   const r = await apiClient.post<{ added: boolean }>(
      `/vendo/devices/${deviceId}/ip-bindings`,
      { mac, comment }
   );
   return r.data;
}

export async function removeIpBinding(
   deviceId: string,
   bindingId: string
): Promise<{ ok: true }> {
   const r = await apiClient.delete<{ ok: true }>(
      `/vendo/devices/${deviceId}/ip-bindings/${bindingId}`
   );
   return r.data;
}

export interface RegisterDevicePayload {
   name: string;
   router?: {
      model?: 'hex-rb750gr3' | 'rb4011' | 'ccr2004' | 'ccr2116';
      /** ISP-uplink port. Defaults server-side to 'ether1' if omitted. */
      wanPort?: string;
      ip?: string;
      username?: string;
      password?: string;
      vpnIp?: string;
      ssid?: string;
      vlanEnabled?: boolean;
      vlan?: {
         id?: number;
         subnet?: string;
         port?: string;
      };
      backhaul?: 'wired' | 'wireless';
      /**
       * Register this MikroTik as LOCAL-FIRST (hybrid) rather than cloud.
       *
       * A local-first vendo sells with no cloud and no tunnel: the NodeMCU
       * writes the session straight onto the router and the router keeps the
       * time. Cloud stays the default and the live cloud MikroTiks are never
       * migrated -- this only ever applies to a NEW registration.
       */
      localMode?: boolean;
   };
   ap?: {
      model?:
         | ''
         | 'tplink-eap110'
         | 'tplink-eap225'
         | 'comfast-cf-ew72'
         | 'comfast-cf-ew71'
         | 'comfast-cf-e380ac'
         | 'other';
   };
   nodeMcu?: {
      mac?: string;
      flashSize?: number;
      firmwareVersion?: string;
      /** VLAN redesign: how the sub-vendo NodeMCU reaches the router. */
      connMode?: 'ethernet' | 'wifi';
      wifiSsid?: string;
      wifiPassword?: string;
   };
   settings?: {
      ratePerPeso?: number;
      defaultDownload?: number;
      defaultUpload?: number;
      pauseEnabled?: boolean;
      roamingEnabled?: boolean;
      autoPauseOnDisconnect?: boolean;
   };
   /** Set when registering a sub-vendo. Server validates ownership + no nesting. */
   parent?: string | null;
   /** VLAN redesign: the parent zone's vlanId this sub attaches to. Required
    *  by the server when the parent has VLAN zones. */
   vlanRef?: number | null;
   /** When true, the license-expiry cron auto-deducts 1 credit at trial/license
    *  end if the owner has wallet balance ≥ 1 and the device is online. */
   autoRenewLicense?: boolean;
}

export interface RegisterDeviceResponse {
   msg: string;
   device: {
      _id: string;
      name: string;
      deviceId: string;
      portalSlug: string;
      [key: string]: unknown;
   };
   portalUrl: string;
   credentials: {
      deviceId: string;
      deviceSecret: string;
   };
}

export async function registerDevice(
   payload: RegisterDevicePayload
): Promise<RegisterDeviceResponse> {
   const response = await apiClient.post<RegisterDeviceResponse>(
      '/vendo/devices',
      payload
   );
   return response.data;
}

/**
 * Claim an OpenWRT ("Lite", Ruijie) router that has self-provisioned and is
 * showing a claim code. Server creates a real VendoDevice owned by the caller,
 * mints a WireGuard peer (best-effort), and arms the agent's next heartbeat.
 */
export interface ClaimOpenwrtResponse {
   ok: boolean;
   device: ApiVendoDevice;
   portalUrl: string;
   wgProvisioned: boolean;
}

export async function claimOpenwrtDevice(payload: {
   claimCode: string;
   name?: string;
   ssid?: string;
   store?: string;
   connMode?: 'wifi' | 'ethernet';
   // The product type chosen in the wizard. MUST be sent for a hybrid box —
   // without it the server defaults to cloud and the box is then never offered
   // the hybrid firmware it needs.
   variant?: 'cloud' | 'hybrid';
}): Promise<ClaimOpenwrtResponse> {
   const response = await apiClient.post<ClaimOpenwrtResponse>(
      '/openwrt/claim',
      payload
   );
   return response.data;
}

/**
 * Switch a Lite vendo between the cloud and hybrid products.
 *
 * Forward (to-hybrid) is the operator's own to take. Backward is admin only and
 * the server enforces it — the UI hides the control, but hiding is not a gate.
 *
 * The NodeMCU is deliberately NOT part of this: a cloud coin board keeps taking
 * coins under a hybrid router (proven on hardware 2026-08-19), so it updates
 * separately afterwards and the shop earns throughout.
 */
export interface MigrateVariantResponse {
   ok: true;
   direction: 'to-hybrid' | 'to-cloud';
   variant: 'cloud' | 'hybrid';
   target: string;
   /** Shown to the operator before and after — includes the NodeMCU note. */
   warnings: string[];
}

export async function migrateDeviceVariant(
   deviceId: string,
   direction: 'to-hybrid' | 'to-cloud'
): Promise<MigrateVariantResponse> {
   const response = await apiClient.post<MigrateVariantResponse>(
      `/vendo/devices/${deviceId}/migrate-variant`,
      { direction }
   );
   return response.data;
}

// Ruijie Rebind — reattach a reset/replacement Lite box to an EXISTING device
// by entering the fresh claim code it shows after reset. Keeps the device's
// identity, license and settings (server reattaches instead of creating new).
export interface RebindResponse {
   ok: true;
   deviceId: string;
   portalUrl: string;
   wgProvisioned: boolean;
}

export async function rebindClaim(
   deviceId: string,
   claimCode: string
): Promise<RebindResponse> {
   const response = await apiClient.post<RebindResponse>(
      '/openwrt/rebind/claim',
      { deviceId, claimCode }
   );
   return response.data;
}

// Rename a Ruijie's broadcast WiFi name (SSID). The box applies it on its next
// heartbeat (agent 1.0.12+); renaming briefly drops WiFi clients.
export async function renameRuijieSsid(
   deviceId: string,
   ssid: string
): Promise<{ ok: true; ssid: string }> {
   const response = await apiClient.post<{ ok: true; ssid: string }>(
      `/openwrt/devices/${deviceId}/ssid`,
      { ssid }
   );
   return response.data;
}

export interface ActiveLiteImage {
   version: string;
   filename: string;
   size: number;
   sha256: string | null;
   downloadUrl: string;
}

/**
 * The active prod image for a Lite board, used by the registration wizard's
 * download link. Resolves to the same .bin the OTA path would flash, so the
 * download URL is never stale (it follows whatever admin activated).
 */
export type DeviceVariant = 'cloud' | 'hybrid';

export async function getActiveLiteImage(
   target:
      | 'ruijie'
      | 'orangepi'
      | 'orangepi-pc'
      | 'orangepi-zero3'
      | 'comfast-ew71'
      | 'comfast-ew72'
      | 'rt2980'
      | 'rt2983'
      | 'newifi-d2'
      | '360-t6m'
      | 'x86-64' = 'ruijie',
   // Which PRODUCT the operator is registering. The server matches on this, so
   // a hybrid registration gets the hybrid image and a cloud one can never be
   // handed it. Defaults to cloud — the direction that cannot migrate a box.
   variant: DeviceVariant = 'cloud'
): Promise<ActiveLiteImage> {
   const response = await apiClient.get<ActiveLiteImage>('/openwrt/image', {
      params: { target, variant },
   });
   return response.data;
}

export interface ResetDeviceStatsResponse {
   reset: boolean;
   deleted: {
      coinDrops: number;
      pendingCoins: number;
      sessions: number;
   };
   // Live sessions (active/paused) that were KEPT — Reset Sales never
   // disconnects a paying customer. Since 2026-09-24 nothing is deleted:
   // the operator's figures restart from salesResetAt and the admin panel
   // keeps the full record, so `deleted.coinDrops/sessions` are always 0.
   keptLiveSessions: number;
   salesResetAt?: string;
}

export async function resetDeviceStats(
   id: string
): Promise<ResetDeviceStatsResponse> {
   const response = await apiClient.post<ResetDeviceStatsResponse>(
      `/vendo/devices/${id}/reset-stats`
   );
   return response.data;
}

// ─────────────────────────────────────────────────────────────────────
// Per-device rate plans (Tier-3-adjacent — wired 2026-04-27)
// ─────────────────────────────────────────────────────────────────────

export interface VendoRateApi {
   _id: string;
   vendoDevice: string;
   owner: string;
   price: number;
   duration: number; // minutes
   label: string;
   description?: string;
   // Which captive-portal tab this rate is surfaced under. Server
   // defaults to 'coin' for pre-migration rows. Optional on the type
   // to tolerate older payloads — UI treats missing as 'coin'.
   type?: 'coin' | 'voucher';
   // VALIDITY (PAUSE), minutes: how long this rate's customers keep FROZEN
   // time. Counted as TOTAL time paused, so reconnecting never resets it.
   // null = use the device's settings.maxPauseDuration. 0 = kept for ever.
   validityMinutes?: number | null;
   createdAt: string;
   updatedAt: string;
}

export const listDeviceRatesApi = async (deviceId: string): Promise<VendoRateApi[]> => {
   const r = await apiClient.get(`/vendo/devices/${deviceId}/rates`);
   return r.data.rates ?? [];
};

export const createDeviceRateApi = async (
   deviceId: string,
   payload: {
      price: number;
      duration: number;
      label: string;
      description?: string;
      type?: 'coin' | 'voucher';
      validityMinutes?: number | null;
   }
): Promise<VendoRateApi> => {
   const r = await apiClient.post(`/vendo/devices/${deviceId}/rates`, payload);
   return r.data.rate;
};

export const updateDeviceRateApi = async (
   rateId: string,
   patch: Partial<{
      price: number;
      duration: number;
      label: string;
      description: string;
      type: 'coin' | 'voucher';
      validityMinutes: number | null;
   }>
): Promise<VendoRateApi> => {
   const r = await apiClient.patch(`/vendo/rates/${rateId}`, patch);
   return r.data.rate;
};

export const deleteDeviceRateApi = async (rateId: string): Promise<void> => {
   await apiClient.delete(`/vendo/rates/${rateId}`);
};

/**
 * Make the target devices' rates of the given type match the source's: a
 * rate at the same price is UPDATED (duration, label, validity), a missing
 * price is ADDED, and with `replace` a price the source lacks is REMOVED.
 * Returns the counts so the modal can show feedback.
 */
export const copyRatesToDevicesApi = async (payload: {
   sourceDeviceId: string;
   targetDeviceIds: string[];
   type: 'coin' | 'voucher';
   replace?: boolean;
}): Promise<{ created: number; updated: number; removed: number; total: number }> => {
   const r = await apiClient.post('/vendo/rates/copy-to-devices', payload);
   return r.data;
};

/** Operator note on a session row. '' clears it. */
export const setSessionNoteApi = async (id: string, note: string): Promise<{ note: string }> => {
   const r = await apiClient.patch(`/vendo/sessions/${id}/note`, { note });
   return r.data;
};

/** Operator note on an Unlimited (whitelist) row. Keyed by the phone's MAC, so
 *  it is written to EVERY vendo that phone is Unlimited on. '' clears it. */
export const setUnlimitedNoteApi = async (mac: string, note: string): Promise<{ note: string }> => {
   const r = await apiClient.patch('/vendo/unlimited/note', { mac, note });
   return r.data;
};

// Owner-scoped voucher dashboard (sidebar Vouchers page on /wifi-vouchers).
// Mirrors the admin endpoints but server filters by req.user.id.

export interface MyVoucherStatsResponse {
   revenue: number;
   withdrawable: number;
   successfulTransactions: number;
   vouchersSoldGcash: number;
   vouchersSoldManual: number;
   vouchersSoldTotal: number;
   vouchersUnredeemed: number;
}

export interface MyVoucherTransactionRow {
   _id: string;
   vendoDevice: { _id: string; name: string; deviceId: string } | null;
   customer: string;
   mac: string;
   ip: string;
   amount: number;
   minutes: number;
   paymentMethod: 'gcash';
   status: 'pending' | 'success' | 'failed';
   voucherCode?: string;
   failureReason?: string;
   platformFee: number;
   xenditFee: number;
   netAmount: number;
   vat: number;
   createdAt: string;
}

// Consolidated dashboard payload — matches the voucher-store
// Home.tsx data shape so we can reuse <CardSalesTop> + <CardSales>
// components verbatim on /wifi-vouchers.
export interface MyVoucherDashboardResponse {
   totalSales: number;
   todayYesterday: { today: number; yesterday: number; percentage: number };
   weekLastWeek: { thisWeek: number; lastWeek: number; percentage: number };
   monthLastMonth: {
      thisMonth: number;
      lastMonth: number;
      percentage: number;
   };
   soldVouchers: number;
   availableVouchers: number;
   totalPayoutReceived: number;
   totalPayoutAvailable: number;
}

export const getMyVoucherDashboardApi =
   async (): Promise<MyVoucherDashboardResponse> => {
      const r = await apiClient.get('/vendo/voucher/dashboard');
      return r.data;
   };

export const createVendoPayoutApi = async (payload: {
   amount: number;
   bank: string;
   accountNumber: string;
   accountName: string;
}) => {
   const r = await apiClient.post('/vendo/voucher/payout-request', payload);
   return r;
};

export interface MyVendoPayoutRow {
   _id: string;
   amount: number;
   disbursementFee: number;
   netAmount: number;
   payoutMethod: string;
   payoutAccount: string;
   payoutAccountNumber: number;
   status: 'processing' | 'settled' | 'failed' | 'cancelled' | 'rejected';
   createdAt: string;
   updatedAt: string;
}

export const listMyVendoPayoutsApi = async (opts: {
   page?: number;
   limit?: number;
   status?: string;
} = {}): Promise<{
   total: number;
   page: number;
   limit: number;
   rows: MyVendoPayoutRow[];
}> => {
   const r = await apiClient.get('/vendo/voucher/my-payouts', {
      params: opts,
   });
   return r.data;
};

export interface MyVoucherSalesTimeseriesResponse {
   days: number;
   series: Array<{ date: string; revenue: number; count: number }>;
}

export const getMySalesTimeseriesApi = async (
   days = 30
): Promise<MyVoucherSalesTimeseriesResponse> => {
   const r = await apiClient.get('/vendo/voucher/sales-timeseries', {
      params: { days },
   });
   return r.data;
};

// Owner-scoped daily vendo revenue (coin + GCash) for the Piso WiFi home chart.
// Optional from/to (YYYY-MM-DD); defaults server-side to the last 30 days.
export interface MyVendoRevenueTimeseriesResponse {
   from: string;
   to: string;
   series: Array<{ date: string; coinRevenue: number; gcashRevenue: number; voucherRevenue?: number }>;
}

export const getMyVendoRevenueTimeseriesApi = async (
   from?: string,
   to?: string,
   /** One vendo's Mongo _id; omitted = every vendo on the account. */
   deviceId?: string
): Promise<MyVendoRevenueTimeseriesResponse> => {
   const params: Record<string, string> = {};
   if (deviceId) params.deviceId = deviceId;
   if (from) params.from = from;
   if (to) params.to = to;
   const r = await apiClient.get('/vendo/revenue-timeseries', { params });
   return r.data;
};

export interface MyTopDevicesResponse {
   devices: Array<{
      _id: string;
      revenue: number;
      count: number;
      name?: string;
      deviceId?: string;
   }>;
}

export const getMyTopDevicesApi = async (
   limit = 5
): Promise<MyTopDevicesResponse> => {
   const r = await apiClient.get('/vendo/voucher/top-devices', {
      params: { limit },
   });
   return r.data;
};

export const getMyVoucherStatsApi = async (opts: {
   from?: string;
   to?: string;
} = {}): Promise<MyVoucherStatsResponse> => {
   const r = await apiClient.get('/vendo/voucher/my-stats', { params: opts });
   return r.data;
};

export const listMyVoucherTransactionsApi = async (opts: {
   page?: number;
   limit?: number;
   status?: 'pending' | 'success' | 'failed';
   deviceId?: string;
   from?: string;
   to?: string;
} = {}): Promise<{
   total: number;
   page: number;
   limit: number;
   rows: MyVoucherTransactionRow[];
}> => {
   const params: Record<string, string | number> = {};
   if (opts.page) params.page = opts.page;
   if (opts.limit) params.limit = opts.limit;
   if (opts.status) params.status = opts.status;
   if (opts.deviceId) params.deviceId = opts.deviceId;
   if (opts.from) params.from = opts.from;
   if (opts.to) params.to = opts.to;
   const r = await apiClient.get('/vendo/voucher/my-transactions', { params });
   return r.data;
};

export interface BulkVoucher {
   _id: string;
   code: string;
   price: number;
   duration: number;
   /** Present on data vouchers (minted from a DataPlanRate template). */
   capMb?: number;
   validUntil?: string | null;
   batchLabel?: string;
   moveable?: boolean;
   /** FREE voucher — the card prints "FREE" instead of a price. */
   free?: boolean;
}

/**
 * Bulk-mint N vouchers (type='manual', sold=false) sized to the chosen
 * voucher rate. Two paths:
 *   - rateId           → time voucher (from a VendoRate row)
 *   - dataPlanRateId   → data voucher (from a DataPlanRate sub-doc)
 * Exactly one must be set; server rejects both/neither with 400.
 */
export const bulkGenerateVouchersApi = async (payload: {
   /** One vendo (Device Settings / sub-login). */
   deviceId?: string;
   /** Over-the-counter page: every vendo the codes work on. */
   deviceIds?: string[];
   rateId?: string;
   dataPlanRateId?: string;
   /** Typed amount + duration instead of a rate (time voucher). */
   custom?: { price: number; durationMinutes: number };
   copies: number;
   // ── advanced, all optional ──
   prefix?: string;
   codeLength?: number;
   validUntil?: string | null;
   batchLabel?: string;
   downloadMbps?: number | null;
   uploadMbps?: number | null;
   validityMinutes?: number | null;
   /** One phone at a time; the newest phone takes the time over. */
   moveable?: boolean;
   // ── Over-the-counter redesign (Ray, 2026-10-14) ──
   /** FREE voucher: no price, the card prints "FREE". Pass custom.durationMinutes. */
   free?: boolean;
   codeMode?: VoucherCodeMode;
   /** codeMode 'own': the exact codes, one each. */
   codes?: string[];
   /** Card layout, kept on the batch so a reprint comes out the same. */
   print?: VoucherPrintLayout;
}): Promise<{
   vouchers: BulkVoucher[];
   batchId?: string;
   print?: VoucherPrintLayout;
   vendoDevices?: { _id: string; name: string }[];
}> => {
   const r = await apiClient.post('/vendo/vouchers/bulk-generate', payload);
   return r.data;
};

export type VoucherCodeMode = 'random' | 'numbered' | 'own';
export type VoucherCardStyle = 'classic' | 'strip' | 'bold';
export interface VoucherPrintLayout {
   storeName: string;
   /** data: URL, '' = none. */
   logoDataUrl: string;
   cardsPerA4: 8 | 12 | 20 | 30;
   cardStyle: VoucherCardStyle;
}

/** The Over-the-counter page's tiles. Owner-wide, or one vendo. */
export async function getVoucherStatsApi(vendo?: string): Promise<{
   unredeemed: number;
   redeemed: number;
   expired: number;
   soldThisMonth: number;
   unredeemedValue: number;
}> {
   const r = await apiClient.get('/vendo/vouchers/stats', { params: vendo ? { vendo } : {} });
   return r.data;
}

/** One print run's card layout (incl. the logo) for a reprint. */
export async function getVoucherBatchApi(id: string): Promise<{
   batch: {
      _id: string;
      label: string;
      free: boolean;
      price: number;
      duration: number;
      print: VoucherPrintLayout;
      vendoDevices: { _id: string; name: string }[];
   };
}> {
   const r = await apiClient.get(`/vendo/vouchers/batches/${id}`);
   return r.data;
}

// ─────────────────────────────────────────────────────────────────────
// Credit wallet (Tier 3 — wired 2026-04-29)
// ─────────────────────────────────────────────────────────────────────

export interface GetCreditsResponse {
   /** Current available wallet balance. */
   credits: number;
   /** Lifetime count of credits consumed for device activation + renewal.
    *  Used as the "Y used" half of the "X credits • Y used" display. */
   used: number;
   /** True for admin accounts: a zero balance never blocks sending credits or
    *  activating your own device, so the UI must not disable those buttons.
    *  The server enforces this regardless of the flag. */
   unlimited?: boolean;
}

export async function getCredits(): Promise<GetCreditsResponse> {
   const response = await apiClient.get<GetCreditsResponse>('/vendo/credits');
   return response.data;
}

export interface VendoStatsResponse {
   totalCoins: number;
   todayCoins: number;
   activeSessions: number;
   totalSessions: number;
   deviceCount: number;
   devicesOnline: number;
   sessionsToday: number;
   /** Filled only when `from`/`to` were provided. */
   sessionsPeriod: number | null;
   creditsUsed: number;
   /** Filled only when `from`/`to` were provided. */
   creditsUsedPeriod: number | null;
}

export interface GetVendoStatsOpts {
   /** ISO date `YYYY-MM-DD` */
   from?: string;
   /** ISO date `YYYY-MM-DD` */
   to?: string;
}

export async function getVendoStats(opts: GetVendoStatsOpts = {}): Promise<VendoStatsResponse> {
   const params: Record<string, string> = {};
   if (opts.from) params.from = opts.from;
   if (opts.to) params.to = opts.to;
   const response = await apiClient.get<VendoStatsResponse>('/vendo/stats', { params });
   return response.data;
}

export type LicenseStatusApi = 'never' | 'trial' | 'active' | 'expired' | 'lifetime';

export interface ActivateTrialResponse {
   success: true;
   licenseStatus: LicenseStatusApi;
   trialStartedAt: string;
   trialEndsAt: string;
}

export async function activateTrial(deviceId: string): Promise<ActivateTrialResponse> {
   const response = await apiClient.post<ActivateTrialResponse>(
      `/vendo/devices/${deviceId}/activate-trial`
   );
   return response.data;
}

export interface ActivateLicenseResponse {
   success: true;
   renewal: boolean;
   licenseStatus: LicenseStatusApi;
   licenseStartedAt: string;
   licenseEndsAt: string;
}

export async function activateLicense(deviceId: string): Promise<ActivateLicenseResponse> {
   const response = await apiClient.post<ActivateLicenseResponse>(
      `/vendo/devices/${deviceId}/activate-license`
   );
   return response.data;
}

export interface MoveLicenseResponse {
   success: true;
   source: { licenseStatus: LicenseStatusApi; licenseEndsAt: string };
   target: { licenseStatus: LicenseStatusApi; licenseEndsAt: string };
}

/** Move a device's remaining paid term onto another of the owner's devices. */
export async function moveLicense(
   deviceId: string,
   targetDeviceId: string
): Promise<MoveLicenseResponse> {
   const response = await apiClient.post<MoveLicenseResponse>(
      `/vendo/devices/${deviceId}/move-license`,
      { targetDeviceId }
   );
   return response.data;
}

// ─────────────────────────────────────────────────────────────────────
// Phase 5 — replace router (re-key the banked license to a new router)
// ─────────────────────────────────────────────────────────────────────

export interface ReplaceRouterResponse {
   success: true;
   changed: boolean;
   /** Present only when changed; the serial the new router reports. */
   newSerial?: string;
   oldSerial?: string | null;
   /** Present only when changed; whether a banked license moved across. */
   licenseRekeyed?: boolean;
   /** Present when nothing changed (same serial still connected). */
   serial?: string;
}

/** Operator swapped this main vendo's router; re-link the license to the
 *  serial the currently-connected router reports (MikroTik REST read). */
export async function replaceRouter(
   deviceId: string
): Promise<ReplaceRouterResponse> {
   const response = await apiClient.post<ReplaceRouterResponse>(
      `/vendo/devices/${deviceId}/replace-router`
   );
   return response.data;
}

// Reflash Router — re-onboard an existing device onto a fresh/reset router.
// start() re-opens the script endpoint; finish() reads the new serial and
// restores (same) or replaces (different) the license, then re-closes it.

export interface ReflashFinishResponse {
   success: true;
   mode: 'restored' | 'replaced';
   serial: string;
   oldSerial: string | null;
   licenseRekeyed: boolean;
   /** Session-replay job armed at finish; total > 0 means there are live
    *  customer sessions to re-grant on the new router. */
   sessionReplay?: {
      status: 'pending' | 'done';
      total: number;
      pendingMacs: string[];
   } | null;
}

export interface ReflashReplayStatus {
   status: 'pending' | 'done';
   total: number;
   done: number;
}

export interface ReflashSnapshot {
   at: string;
   serial: string;
   model: string;
   wanPort: string;
}

export async function reflashHistory(
   deviceId: string
): Promise<{ history: ReflashSnapshot[] }> {
   const response = await apiClient.get<{ history: ReflashSnapshot[] }>(
      `/vendo/devices/${deviceId}/reflash/history`
   );
   return response.data;
}

export async function reflashRestore(
   deviceId: string,
   at: string
): Promise<{ success: true; restoredTo: { serial: string; model: string } }> {
   const response = await apiClient.post<{
      success: true;
      restoredTo: { serial: string; model: string };
   }>(`/vendo/devices/${deviceId}/reflash/restore`, { at });
   return response.data;
}

export async function reflashReplayRun(
   deviceId: string
): Promise<{ success: true }> {
   const response = await apiClient.post<{ success: true }>(
      `/vendo/devices/${deviceId}/reflash/replay-run`
   );
   return response.data;
}

export async function reflashReplayStatus(
   deviceId: string
): Promise<ReflashReplayStatus> {
   const response = await apiClient.get<ReflashReplayStatus>(
      `/vendo/devices/${deviceId}/reflash/replay-status`
   );
   return response.data;
}

export async function reflashStart(
   deviceId: string,
   model?: string
): Promise<{ success: true }> {
   const response = await apiClient.post<{ success: true }>(
      `/vendo/devices/${deviceId}/reflash/start`,
      model ? { model } : undefined
   );
   return response.data;
}

export async function reflashFinish(
   deviceId: string
): Promise<ReflashFinishResponse> {
   const response = await apiClient.post<ReflashFinishResponse>(
      `/vendo/devices/${deviceId}/reflash/finish`
   );
   return response.data;
}

// ─────────────────────────────────────────────────────────────────────
// Phase 6 — theft / ownership conflict
// ─────────────────────────────────────────────────────────────────────

export interface OwnershipClaim {
   _id: string;
   hardwareKey: string;
   serial: string | null;
   /** The owner's device (_id) that holds this license — binds the claim to a
    *  specific device card so we can show the "suspected stolen" banner. */
   originalDevice: string | null;
   claimantDeviceId: string;
   status: 'pending' | 'granted' | 'denied';
   publicIp: string | null;
   isp: string | null;
   createdAt: string;
}

export async function getOwnershipClaims(): Promise<{ claims: OwnershipClaim[] }> {
   const response = await apiClient.get<{ claims: OwnershipClaim[] }>(
      '/vendo/ownership-claims'
   );
   return response.data;
}

export async function grantOwnershipClaim(id: string): Promise<{ success: true }> {
   const response = await apiClient.post<{ success: true }>(
      `/vendo/ownership-claims/${id}/grant`
   );
   return response.data;
}

export async function denyOwnershipClaim(id: string): Promise<{ success: true }> {
   const response = await apiClient.post<{ success: true }>(
      `/vendo/ownership-claims/${id}/deny`
   );
   return response.data;
}

/** Owner flags (or un-flags) their own device lost/stolen. */
export async function markDeviceStolen(
   deviceId: string,
   stolen: boolean
): Promise<{ success: true; markedStolen: boolean }> {
   const response = await apiClient.post<{ success: true; markedStolen: boolean }>(
      `/vendo/devices/${deviceId}/mark-stolen`,
      { stolen }
   );
   return response.data;
}

// ─────────────────────────────────────────────────────────────────────
// Distributor partners + credit inquiries (Tier 3 — wired 2026-04-29)
// ─────────────────────────────────────────────────────────────────────

export type PartnerTier = 'bronze' | 'silver' | 'gold' | 'diamond' | 'legendary';

export interface ApiPartner {
   _id: string;
   displayName: string;
   businessName: string;
   region: string;
   city: string;
   slug?: string; // shareable public handle → smartfi.ph/p/<slug>
   photoUrl?: string;
   blurb?: string;
   contactPhone?: string;
   facebookUrl?: string;
   defaultPricePerCredit: number;
   tier: PartnerTier;
   cumulativeCreditsSold: number;
   availableCredits: number;
   // System-controlled prices (same for every distributor). Show the reseller
   // price to an approved reseller of this distributor, the base price to others.
   baseCreditPrice?: number;
   resellerPricePerCredit?: number;
   /** The distributor's own account (for "Message"). */
   user?: string;
   /** Has SmartFi open right now (connected to the chat) — listed first, green dot (Ray 2026-10-27). */
   online?: boolean;
}

export async function listPartners(): Promise<ApiPartner[]> {
   const response = await apiClient.get<{ partners: ApiPartner[] }>('/vendo/partners');
   return response.data.partners;
}

/**
 * One seller resolved by their shareable handle — distributor OR reseller.
 * Backs the public seller page at /s/<slug>, which is where the landing
 * link (smartfi.ph/p/<slug>) now sends shoppers. No auth: a stranger
 * arriving from Facebook must see the seller before signing in.
 */
export interface ApiPublicPartner {
   id: string;
   kind: 'distributor' | 'reseller';
   displayName: string;
   slug?: string;
   photoUrl?: string;
   blurb?: string;
   contactPhone?: string;
   facebookUrl?: string;
   region?: string;
   city?: string;
   tier?: PartnerTier;
   canSell: boolean;
   availableCredits: number;
   basePricePerCredit: number;
   /** Distributors only — what an APPROVED reseller of theirs pays. */
   resellerPricePerCredit?: number;
}

export async function getPartnerBySlugApi(
   slug: string
): Promise<ApiPublicPartner> {
   const res = await apiClient.get<{ partner: ApiPublicPartner }>(
      `/vendo/partners/slug/${encodeURIComponent(slug)}`
   );
   return res.data.partner;
}

export interface PartnerPaymentOptions {
   bankAccounts: DistributorBankAccount[];
   gcashAccounts: DistributorGcashAccount[];
   contactPhone?: string;
   facebookUrl?: string;
}

export async function getPartnerPaymentOptions(
   partnerId: string
): Promise<PartnerPaymentOptions> {
   const response = await apiClient.get<PartnerPaymentOptions>(
      `/vendo/partners/${partnerId}/payment-options`
   );
   return response.data;
}

export interface DistributorBankAccount {
   bankName: string;
   accountNumber: string;
   accountName: string;
}

export interface DistributorGcashAccount {
   number: string;
   name: string;
}

export interface DistributorProfile extends ApiPartner {
   bankAccounts: DistributorBankAccount[];
   gcashAccounts: DistributorGcashAccount[];
   isActive: boolean;
   user: string;
}

export interface DistributorProfilePatch {
   displayName?: string;
   blurb?: string;
   photoUrl?: string;
   contactPhone?: string;
   facebookUrl?: string;
   defaultPricePerCredit?: number;
   bankAccounts?: DistributorBankAccount[];
   gcashAccounts?: DistributorGcashAccount[];
}

export async function getMyDistributorProfile(): Promise<DistributorProfile> {
   const response = await apiClient.get<{ partner: DistributorProfile }>(
      '/vendo/distributor/me/profile'
   );
   return response.data.partner;
}

export async function updateMyDistributorProfile(
   patch: DistributorProfilePatch
): Promise<DistributorProfile> {
   const response = await apiClient.put<{ partner: DistributorProfile }>(
      '/vendo/distributor/me/profile',
      patch
   );
   return response.data.partner;
}

export interface CreditReleaseStats {
   monthlyLimit: number;
   releasedThisMonth: number;
   availableToRelease: number;
   partnerInventory: number;
}

export async function getCreditReleaseStats(): Promise<CreditReleaseStats> {
   const response = await apiClient.get<{ stats: CreditReleaseStats }>(
      '/vendo/credit-release-stats'
   );
   return response.data.stats;
}

export type InquiryStatusApi = 'pending' | 'approved' | 'fulfilled' | 'rejected';

export interface ApiInquiry {
   _id: string;
   buyer: string;
   // A distributor sale carries `distributor`; a reseller sale carries
   // `reseller` with sellerKind='reseller'. `distributor` populated to a
   // small profile object on buyer-scope reads.
   sellerKind?: 'distributor' | 'reseller';
   distributor?: string;
   reseller?: string;
   requestedCredits: number;
   proposedPricePerCredit: number;
   negotiatedPricePerCredit?: number;
   totalAmount?: number;
   status: InquiryStatusApi;
   notes?: string;
   approvedAt?: string;
   fulfilledAt?: string;
   rejectedAt?: string;
   paymentProofUrl?: string;
   paymentSubmittedAt?: string;
   createdAt: string;
   updatedAt: string;
}

export async function createInquiry(input: {
   distributorPartnerId: string;
   requestedCredits: number;
   notes?: string;
   paymentProofUrl?: string;
}): Promise<ApiInquiry> {
   const response = await apiClient.post<{ inquiry: ApiInquiry }>(
      '/vendo/inquiries',
      input
   );
   return response.data.inquiry;
}

export async function submitInquiryProof(
   id: string,
   paymentProofUrl: string
): Promise<ApiInquiry> {
   const response = await apiClient.post<{ inquiry: ApiInquiry }>(
      `/vendo/inquiries/${id}/submit-proof`,
      { paymentProofUrl }
   );
   return response.data.inquiry;
}

export async function listInquiries(opts: {
   scope: 'buyer' | 'distributor';
   status?: string;
   limit?: number;
} = { scope: 'buyer' }): Promise<ApiInquiry[]> {
   const params: Record<string, string | number> = { scope: opts.scope };
   if (opts.status) params.status = opts.status;
   if (opts.limit) params.limit = opts.limit;
   const response = await apiClient.get<{ inquiries: ApiInquiry[] }>('/vendo/inquiries', {
      params,
   });
   return response.data.inquiries;
}

export async function approveInquiry(
   id: string,
   negotiatedPricePerCredit: number
): Promise<ApiInquiry> {
   const response = await apiClient.post<{ inquiry: ApiInquiry }>(
      `/vendo/inquiries/${id}/approve`,
      { negotiatedPricePerCredit }
   );
   return response.data.inquiry;
}

export async function fulfillInquiry(id: string): Promise<ApiInquiry> {
   const response = await apiClient.post<{ inquiry: ApiInquiry }>(
      `/vendo/inquiries/${id}/fulfill`
   );
   return response.data.inquiry;
}

export async function rejectInquiry(id: string, reason?: string): Promise<ApiInquiry> {
   const response = await apiClient.post<{ inquiry: ApiInquiry }>(
      `/vendo/inquiries/${id}/reject`,
      { reason }
   );
   return response.data.inquiry;
}

// ─────────────────────────────────────────────────────────────────────
// Distributor application intake (Tier 3 — wired 2026-04-29)
// ─────────────────────────────────────────────────────────────────────

export type ApplicationStatusApi = 'pending' | 'approved' | 'rejected';

export interface DistributorQualifyingAnswers {
   hasPisoWifiExperience: boolean;
   hasMikrotikKnowledge: boolean;
   isPriorDistributor: boolean;
   hasPhysicalLocation: boolean;
   willProvideSupport: boolean;
   hasWaitingCustomers: boolean;
   willSellInOwnRegion: boolean;
   hasOnlinePresence: boolean;
   canCommitMonthly: boolean;
   willDoOwnSetup: boolean;
}

export interface ApiDistributorApplication {
   _id: string;
   applicant: string;
   displayName: string;
   businessName: string;
   region: string;
   city: string;
   contactPhone: string;
   facebookUrl: string;
   govIdUrl: string;
   mayorsPermitUrl?: string;
   bir2303Url?: string;
   proofOfAddressUrl?: string;
   qualifyingAnswers: DistributorQualifyingAnswers;
   defaultPricePerCredit: number;
   status: ApplicationStatusApi;
   rejectionReason?: string;
   partnerCreated?: string;
   createdAt: string;
   updatedAt: string;
   /** Is the distributor shop from this approval still switched on? (server 2026-10-27) */
   partnerActive?: boolean | null;
}

export interface SubmitDistributorApplicationPayload {
   displayName: string;
   businessName: string;
   region: string;
   city: string;
   contactPhone: string;
   facebookUrl: string;
   govIdUrl: string;
   mayorsPermitUrl?: string;
   bir2303Url?: string;
   proofOfAddressUrl?: string;
   qualifyingAnswers: DistributorQualifyingAnswers;
   // Vestigial — pricing is system-controlled now; no longer collected at apply.
   defaultPricePerCredit?: number;
   // Wizard step 2 — advance credit-bundle purchase. Required; the buy posts
   // when admin approves the application.
   bundleName: string;
   paymentMethod: 'gcash' | 'bank-transfer';
   paymentProofUrl: string;
   referenceNumber?: string;
   paidAt?: string; // ISO
}

export async function submitDistributorApplication(
   payload: SubmitDistributorApplicationPayload
): Promise<ApiDistributorApplication> {
   const response = await apiClient.post<{ application: ApiDistributorApplication }>(
      '/vendo/distributor-applications',
      payload
   );
   return response.data.application;
}

export async function listMyApplications(): Promise<ApiDistributorApplication[]> {
   const response = await apiClient.get<{ applications: ApiDistributorApplication[] }>(
      '/vendo/distributor-applications/mine'
   );
   return response.data.applications;
}

// ─────────────────────────────────────────────────────────────────────
// Credit packages (distributor purchase flow — Tier 3 wired 2026-04-29)
// ─────────────────────────────────────────────────────────────────────

// Bundle name (free-form). Was previously a fixed 6-tier enum; now
// admin-editable so we accept any string.
export type PackageTier = string;

export interface CreditPackageTier {
   name: string;
   credits: number;
   amount: number;
   pricePerCredit: number;
   icon?: string;
   savings?: number;
   // Loyalty points a distributor earns buying this bundle (points system).
   points?: number;
   // Extra promo credits on top of `credits`, highlighted in the marketplace.
   bonusCredits?: number;
}

export type CreditPurchaseStatus =
   | 'pending'
   | 'payment-submitted'
   | 'approved'
   | 'rejected';

export interface ApiCreditPurchase {
   _id: string;
   buyer: string;
   tier: PackageTier;
   credits: number;
   amount: number;
   pricePerCredit: number;
   status: CreditPurchaseStatus;
   paymentMethod?: 'gcash' | 'bank-transfer';
   paymentProofUrl?: string;
   referenceNumber?: string;
   paidAt?: string;
   proofSubmittedAt?: string;
   approvedAt?: string;
   rejectedAt?: string;
   createdAt: string;
   updatedAt: string;
}

export interface BankAccountPublic {
   bankName: string;
   accountNumber: string;
   accountName: string;
}
export interface GcashAccountPublic {
   number: string;
   name: string;
}
export interface PaymentDetails {
   bankAccounts: BankAccountPublic[];
   gcashAccounts: GcashAccountPublic[];
   paymentInstructions: string;
}

export interface InitiateCreditPackageResponse {
   purchase: ApiCreditPurchase;
   paymentDetails: PaymentDetails;
}

export async function getCreditPackageTiers(): Promise<CreditPackageTier[]> {
   const r = await apiClient.get<{ tiers: CreditPackageTier[] }>(
      '/vendo/credit-packages/tiers'
   );
   return r.data.tiers;
}

/**
 * Read-only fetch of the SmartFi payment accounts to show in the buy modal —
 * does NOT create a purchase row. The purchase is only created on submit (once
 * proof is uploaded), so abandoned checkouts leave no orphan "awaiting proof".
 */
export async function getCreditPackagePaymentDetails(): Promise<PaymentDetails> {
   const r = await apiClient.get<{ details: PaymentDetails }>(
      '/vendo/credit-packages/payment-details'
   );
   return r.data.details;
}

export async function initiateCreditPackage(
   tier: PackageTier
): Promise<InitiateCreditPackageResponse> {
   const r = await apiClient.post<InitiateCreditPackageResponse>(
      '/vendo/credit-packages/initiate',
      { tier }
   );
   return r.data;
}

export interface SubmitCreditPackageProofPayload {
   paymentMethod: 'gcash' | 'bank-transfer';
   paymentProofUrl: string;
   referenceNumber?: string;
   paidAt?: string; // ISO
}

export async function submitCreditPackageProof(
   purchaseId: string,
   payload: SubmitCreditPackageProofPayload
): Promise<ApiCreditPurchase> {
   const r = await apiClient.post<{ purchase: ApiCreditPurchase }>(
      `/vendo/credit-packages/${purchaseId}/submit-proof`,
      payload
   );
   return r.data.purchase;
}

export async function listMyCreditPackages(): Promise<ApiCreditPurchase[]> {
   const r = await apiClient.get<{ purchases: ApiCreditPurchase[] }>(
      '/vendo/credit-packages/mine'
   );
   return r.data.purchases;
}

/* ─── Device ownership transfer (peer-to-peer + sale, Flow A) ───── */

export type TransferPaymentType = 'free' | 'full-cash' | 'installment';
export type TransferStatus =
   | 'pending'
   | 'accepted'
   | 'rejected'
   | 'cancelled'
   | 'expired';

export interface ApiDeviceTransfer {
   _id: string;
   device:
      | { _id: string; name?: string; deviceId?: string; portalSlug?: string }
      | string;
   fromOwner: { _id: string; name?: string; email: string } | string;
   toOwner: { _id: string; name?: string; email: string } | string | null;
   toEmail: string;
   status: TransferStatus;
   note: string | null;
   paymentType: TransferPaymentType;
   fullCashAmount: number | null;
   fullCashPaidAt: string | null;
   claimCode: string | null;
   /** Only present on outgoing pending rows — the seller's copy of the
    *  signed JWT used to build the buyer's `/transfers/handoff?token=`
    *  link. Verified server-side; rebuilt each list call. */
   signupToken?: string;
   initiatedAt: string;
   expiresAt: string;
   acceptedAt: string | null;
   rejectedAt: string | null;
   cancelledAt: string | null;
}

export interface InitiateTransferPayload {
   toEmail: string;
   paymentType: 'free' | 'full-cash';
   fullCashAmount?: number;
   note?: string;
   /** Flow B opt-in. When true, the server returns a single-use claim
    *  code the seller can read aloud to the buyer. */
   generateCode?: boolean;
}

export async function initiateTransferApi(
   deviceId: string,
   payload: InitiateTransferPayload
): Promise<{
   transferId: string;
   signupToken: string;
   claimCode: string | null;
   expiresAt: string;
}> {
   const r = await apiClient.post(
      `/vendo/devices/${deviceId}/transfer/initiate`,
      payload
   );
   return r.data;
}

export interface TransferHandoffData {
   transfer: ApiDeviceTransfer;
   toEmail: string;
   hasExistingAccount: boolean;
}

export async function verifyTransferHandoffApi(
   token: string
): Promise<TransferHandoffData> {
   const r = await apiClient.post<TransferHandoffData>(
      '/auth/transfer-token/verify',
      { token }
   );
   return r.data;
}

export interface SignupFromTransferResp {
   token: string;
   user: { id: string; email: string; name: string; verified: boolean };
   msg?: string;
   error?: string;
   code?: string;
}

export async function signupFromTransferApi(payload: {
   token: string;
   password: string;
   name?: string;
}): Promise<SignupFromTransferResp> {
   const r = await apiClient.post<SignupFromTransferResp>(
      '/auth/signup-from-transfer',
      payload
   );
   return r.data;
}

/**
 * Flow B: buyer types the 8-char code from the seller. Server looks up
 * the matching pending transfer, verifies the recipient email matches
 * the logged-in user, and returns the transfer for the Review modal.
 */
export async function claimTransferByCodeApi(
   code: string
): Promise<ApiDeviceTransfer> {
   const r = await apiClient.post<{ transfer: ApiDeviceTransfer }>(
      '/vendo/transfers/claim',
      { code }
   );
   return r.data.transfer;
}

export async function cancelTransferApi(
   deviceId: string
): Promise<{ msg: string; transferId: string }> {
   const r = await apiClient.post(
      `/vendo/devices/${deviceId}/transfer/cancel`,
      {}
   );
   return r.data;
}

export async function acceptTransferApi(
   transferId: string
): Promise<{ msg: string; transferId: string; deviceId: string }> {
   const r = await apiClient.post(`/vendo/transfers/${transferId}/accept`, {});
   return r.data;
}

export async function rejectTransferApi(
   transferId: string
): Promise<{ msg: string; transferId: string }> {
   const r = await apiClient.post(`/vendo/transfers/${transferId}/reject`, {});
   return r.data;
}

export async function listTransfersApi(
   scope: 'incoming' | 'outgoing',
   includeAll = false
): Promise<ApiDeviceTransfer[]> {
   const r = await apiClient.get<{ transfers: ApiDeviceTransfer[] }>(
      '/vendo/transfers',
      {
         params: { scope, ...(includeAll ? { includeAll: '1' } : {}) },
      }
   );
   return r.data.transfers;
}

// ─── Points / loyalty system ───────────────────────────────────────────

// 'upgraded' = the account became a DISTRIBUTOR, which closes the reseller
// role — a user is never both at once.
export type ResellerStatus =
   | 'pending'
   | 'approved'
   | 'rejected'
   | 'withdrawn'
   | 'upgraded';

export interface ApiResellerStats {
   points: number;
   creditsLeft: number;
   creditsSold: number;
   revenue: number;
   transactions: number;
}

export interface ApiReseller {
   _id: string;
   user: string;
   distributor: string;
   status: ResellerStatus;
   displayName: string;
   contactPhone?: string;
   facebookUrl?: string;
   slug?: string; // shareable public handle → smartfi.ph/p/<slug>
   photoUrl?: string;
   blurb?: string;
   note?: string;
   govIdUrl?: string;
   checklist?: Record<string, boolean>;
   isActive: boolean;
   approvedAt?: string | null;
   rejectedAt?: string | null;
   rejectionReason?: string;
   createdAt: string;
   stats?: ApiResellerStats;
}

export interface ResellerProfilePatch {
   displayName?: string;
   blurb?: string;
   photoUrl?: string;
   contactPhone?: string;
   facebookUrl?: string;
}

export async function updateMyResellerProfileApi(
   patch: ResellerProfilePatch
): Promise<ApiReseller> {
   const response = await apiClient.put<{ reseller: ApiReseller }>(
      '/vendo/reseller/me/profile',
      patch
   );
   return response.data.reseller;
}

export interface ApiPointsLedgerRow {
   _id: string;
   delta: number;
   reason: string;
   balanceAfter: number;
   refType?: string;
   refId?: string;
   note?: string;
   createdAt: string;
}

export interface ApiIncentive {
   _id: string;
   name: string;
   category?: 'credit' | 'cash' | 'gadget' | 'appliance' | 'travel';
   pointsCost: number;
   imageUrl?: string;
   description?: string;
   active: boolean;
   sortOrder: number;
}

export type RedemptionStatus = 'pending' | 'fulfilled' | 'rejected';

export interface ApiRedemption {
   _id: string;
   user: string;
   incentive: string;
   incentiveName: string;
   imageUrl?: string;
   pointsCost: number;
   status: RedemptionStatus;
   note?: string;
   adminNote?: string;
   fulfilledAt?: string;
   rejectedAt?: string;
   createdAt: string;
}

export async function applyResellerApi(input: {
   distributorId: string;
   displayName: string;
   contactPhone?: string;
   facebookUrl?: string;
   note?: string;
   govIdUrl?: string;
   checklist?: Record<string, boolean>;
}): Promise<{ reseller: ApiReseller }> {
   const res = await apiClient.post('/vendo/reseller-applications', input);
   return res.data;
}

export async function getMyResellerApi(): Promise<{ reseller: ApiReseller | null }> {
   const res = await apiClient.get('/vendo/reseller/me');
   return res.data;
}

/** The applicant cancels their own PENDING reseller request, freeing them to
 *  apply to another distributor. */
export async function cancelMyResellerApi(): Promise<{ reseller: ApiReseller }> {
   const res = await apiClient.delete('/vendo/reseller-applications/me');
   return res.data;
}

// ── Reseller-as-seller: public list + buy-from-reseller + reseller inbox ──

export interface ApiPublicReseller {
   _id: string;
   displayName: string;
   slug?: string;
   photoUrl?: string;
   blurb?: string;
   contactPhone?: string;
   facebookUrl?: string;
   upline?: string; // upline distributor's display name
   availableCredits: number;
}

export async function listPublicResellersApi(): Promise<ApiPublicReseller[]> {
   const res = await apiClient.get<{ resellers: ApiPublicReseller[] }>(
      '/vendo/resellers'
   );
   return res.data.resellers;
}

export async function createResellerInquiryApi(input: {
   resellerId: string;
   requestedCredits: number;
   notes?: string;
   paymentProofUrl?: string;
}): Promise<ApiInquiry> {
   const res = await apiClient.post<{ inquiry: ApiInquiry }>(
      '/vendo/reseller-inquiries',
      input
   );
   return res.data.inquiry;
}

export async function listResellerInboxApi(
   status?: string
): Promise<ApiInquiry[]> {
   const res = await apiClient.get<{ inquiries: ApiInquiry[] }>(
      '/vendo/reseller/me/inquiries',
      { params: status ? { status } : undefined }
   );
   return res.data.inquiries;
}

export async function approveResellerInquiryApi(id: string): Promise<ApiInquiry> {
   const res = await apiClient.post<{ inquiry: ApiInquiry }>(
      `/vendo/reseller-inquiries/${id}/approve`
   );
   return res.data.inquiry;
}

export async function fulfillResellerInquiryApi(id: string): Promise<ApiInquiry> {
   const res = await apiClient.post<{ inquiry: ApiInquiry }>(
      `/vendo/reseller-inquiries/${id}/fulfill`
   );
   return res.data.inquiry;
}

export async function rejectResellerInquiryApi(
   id: string,
   reason?: string
): Promise<ApiInquiry> {
   const res = await apiClient.post<{ inquiry: ApiInquiry }>(
      `/vendo/reseller-inquiries/${id}/reject`,
      { reason }
   );
   return res.data.inquiry;
}

export async function listMyResellersApi(
   status?: ResellerStatus
): Promise<{ resellers: ApiReseller[] }> {
   const res = await apiClient.get('/vendo/distributor/me/resellers', {
      params: status ? { status } : undefined,
   });
   return res.data;
}

export async function decideResellerApi(
   id: string,
   decision: 'approve' | 'reject',
   reason?: string
): Promise<{ reseller: ApiReseller }> {
   const res = await apiClient.post(
      `/vendo/distributor/me/resellers/${id}/${decision}`,
      decision === 'reject' ? { reason } : {}
   );
   return res.data;
}

export interface ApiPointsMechanics {
   pointsPerCredit: number;
   distributorPointsPerCredit: number;
   resellerPointsPerCredit: number;
   referralPointsPerCredit: number;
}

export async function getMyPointsApi(): Promise<{
   balance: number;
   ledger: ApiPointsLedgerRow[];
   mechanics?: ApiPointsMechanics;
}> {
   const res = await apiClient.get('/vendo/points/me');
   return res.data;
}

export async function listIncentivesApi(): Promise<{ incentives: ApiIncentive[] }> {
   const res = await apiClient.get('/vendo/incentives');
   return res.data;
}

export async function getMyReferralApi(): Promise<{ code: string }> {
   const res = await apiClient.get('/vendo/referral/me');
   return res.data;
}

export async function convertPointsApi(
   credits: number
): Promise<{ creditsGranted: number; pointsSpent: number; pointsBalance: number }> {
   const res = await apiClient.post('/vendo/points/convert', { credits });
   return res.data;
}

export async function redeemIncentiveApi(
   incentiveId: string,
   note?: string
): Promise<{ redemption: ApiRedemption; pointsBalance: number }> {
   const res = await apiClient.post(`/vendo/incentives/${incentiveId}/redeem`, { note });
   return res.data;
}

export async function listMyRedemptionsApi(): Promise<{
   redemptions: ApiRedemption[];
}> {
   const res = await apiClient.get('/vendo/redemptions/me');
   return res.data;
}

export interface ApiCreditTransfer {
   _id: string;
   from: string;
   to: string;
   fromEmail: string;
   toEmail: string;
   amount: number;
   note?: string;
   createdAt: string;
}

export async function lookupTransferRecipientApi(
   email: string
): Promise<{
   recipient: { name?: string; email: string; isDistributor?: boolean };
}> {
   const res = await apiClient.get('/vendo/credits/recipient', {
      params: { email },
   });
   return res.data;
}

export async function transferCreditApi(input: {
   toEmail: string;
   amount: number;
   note?: string;
}): Promise<{
   /** Null when an admin GAVE the credits — no transfer took place. */
   transfer: ApiCreditTransfer | null;
   /** True when an admin gave rather than transferred. */
   issued: boolean;
   /** How much came out of the sender's own balance (the rest was created). */
   spentFromBalance: number;
   balance: number;
}> {
   const res = await apiClient.post('/vendo/credits/transfer', input);
   return res.data;
}

export async function listMyTransfersApi(): Promise<{
   transfers: ApiCreditTransfer[];
   me: string;
}> {
   const res = await apiClient.get('/vendo/credits/transfers/me');
   return res.data;
}

/** One completed credit sale — a fulfilled inquiry where the caller was
 *  the selling distributor (server pre-shapes the row for the table). */
export interface ApiCreditSale {
   id: string;
   buyerName: string;
   buyerEmail: string;
   credits: number;
   pricePerCredit: number;
   total: number;
   fulfilledAt: string;
}

export async function listMySalesApi(): Promise<{ sales: ApiCreditSale[] }> {
   const res = await apiClient.get('/vendo/credits/sales/me');
   return res.data;
}

/** One voucher row from GET /vendo/vouchers (device-scoped list —
 *  reprint table + redeemed history in the device voucher panel). */
export interface ApiDeviceVoucher {
   _id: string;
   code: string;
   price: number;
   duration: number;
   capMb?: number | null;
   type: 'gcash' | 'manual';
   sold: boolean;
   createdAt: string;
   redeemedAt?: string | null;
   updatedAt: string;
   prefix?: string;
   batchLabel?: string;
   validUntil?: string | null;
   downloadMbps?: number | null;
   uploadMbps?: number | null;
   validityMinutes?: number | null;
   moveable?: boolean;
   /** Moveable: the phone currently holding the time. */
   activeMac?: string;
   /** The vendo the code was printed for (name filled in by the server). */
   vendoDevice?: { _id: string; name?: string } | string | null;
   /** Every vendo the code works on (Over-the-counter runs). Empty = any of the owner's. */
   vendoDevices?: { _id: string; name?: string; deviceId?: string }[];
   free?: boolean;
   codeMode?: VoucherCodeMode;
   /** The print run — layout without the logo (fetch getVoucherBatchApi to reprint). */
   batch?: { _id: string; label?: string; free?: boolean; print?: Omit<VoucherPrintLayout, 'logoDataUrl'> } | string | null;
}

/** Delete one of my vouchers. A USED code is refused (409) unless includeRedeemed. */
export async function deleteVoucherApi(id: string, includeRedeemed = false): Promise<{ deleted: number }> {
   const res = await apiClient.delete(`/vendo/vouchers/${id}`, {
      params: includeRedeemed ? { includeRedeemed: '1' } : {},
   });
   return res.data;
}

/** Delete many. Used codes are skipped unless includeRedeemed. */
export async function bulkDeleteVouchersApi(
   ids: string[],
   includeRedeemed = false
): Promise<{ deleted: number; skipped: number }> {
   const res = await apiClient.post('/vendo/vouchers/bulk-delete', { ids, includeRedeemed });
   return res.data;
}

/** One price+duration pair that actually has vouchers behind it.
 *
 *  A voucher carries no reference to the rate that minted it — bulk
 *  print copies price/duration onto the row — so a "rate" in the
 *  inventory drawer IS the price+duration pair. That also means a batch
 *  whose rate was since deleted or re-priced still groups correctly. */
export interface ApiVoucherRateGroup {
   price: number;
   duration: number;
   capMb: number | null;
   count: number;
}

export async function listVoucherRateGroupsApi(params: {
   deviceId?: string;
   status: 'unredeemed' | 'redeemed';
}): Promise<{ groups: ApiVoucherRateGroup[]; batches?: { label: string; count: number }[] }> {
   const res = await apiClient.get('/vendo/vouchers/rate-groups', { params });
   return res.data;
}

/** Pull the current list down as a spreadsheet.
 *
 *  Fetched through apiClient rather than pointed at by a plain <a href>
 *  so the request carries the session the same way every other call
 *  does — a bare link would arrive unauthenticated. */
export async function exportVouchersCsvApi(params: {
   deviceId?: string;
   status: 'unredeemed' | 'redeemed';
   price?: number;
   duration?: number;
}): Promise<{ blob: Blob; filename: string }> {
   const res = await apiClient.get('/vendo/vouchers/export.csv', {
      params,
      responseType: 'blob',
   });
   const disposition = String(res.headers['content-disposition'] || '');
   const match = disposition.match(/filename="([^"]+)"/);
   return {
      blob: res.data as Blob,
      filename: match?.[1] || `vouchers-${params.status}.csv`,
   };
}

export async function listDeviceVouchersApi(params: {
   /** Omit for the caller's vouchers across ALL devices. */
   deviceId?: string;
   /** 'live' = unused and not past its date; 'expired' = unused but past it.
    *  Omit for every status. */
   status?: 'unredeemed' | 'redeemed' | 'live' | 'expired';
   page?: number;
   limit?: number;
   /** Narrow to one rate. Both or neither — a lone price filters nothing. */
   price?: number;
   duration?: number;
   /** Narrow to one batch label. */
   batch?: string;
   /** 'time' or 'data' vouchers only. */
   kind?: 'time' | 'data';
   /** Owner-wide list: narrow to one vendo. */
   vendo?: string;
   /** Code contains (letters/digits, case-insensitive). */
   q?: string;
}): Promise<{
   vouchers: ApiDeviceVoucher[];
   total: number;
   page: number;
   limit: number;
}> {
   const res = await apiClient.get('/vendo/vouchers', { params });
   return res.data;
}

/* ── Client sub-login (basic portal for a sold vendo's buyer) ─────── */
export interface ApiClientAccess {
   username: string;
   active: boolean;
   createdAt: string;
   permissions?: { renameVendo?: boolean; renameSsid?: boolean };
}

export async function getClientAccessApi(
   deviceId: string,
   opts?: { reveal?: boolean }
): Promise<{ access: (ApiClientAccess & { password?: string | null }) | null }> {
   const res = await apiClient.get(`/vendo/devices/${deviceId}/client-access`, {
      params: opts?.reveal ? { reveal: '1' } : {},
   });
   return res.data;
}

export async function createClientAccessApi(
   deviceId: string
): Promise<{ access: ApiClientAccess; password: string }> {
   const res = await apiClient.post(`/vendo/devices/${deviceId}/client-access`);
   return res.data;
}

export async function updateClientAccessApi(
   deviceId: string,
   patch: {
      username?: string;
      resetPassword?: boolean;
      active?: boolean;
      permissions?: { renameVendo?: boolean; renameSsid?: boolean };
   }
): Promise<{ access: ApiClientAccess; password?: string }> {
   const res = await apiClient.patch(
      `/vendo/devices/${deviceId}/client-access`,
      patch
   );
   return res.data;
}

export async function deleteClientAccessApi(
   deviceId: string
): Promise<{ ok: boolean }> {
   const res = await apiClient.delete(`/vendo/devices/${deviceId}/client-access`);
   return res.data;
}

/** Make ALL sub-vendos of this main follow the main's design again. */
export async function syncSubPortalThemesApi(
   deviceId: string
): Promise<{ ok: boolean; subs: number }> {
   const res = await apiClient.post(`/vendo/devices/${deviceId}/portal-sync-subs`);
   return res.data;
}

// ── Live coin ticker ────────────────────────────────────────────────────────
export interface CoinRow {
   id: string;
   amount: number;
   minutes: number;
   mac: string | null;
   kind: 'new' | 'extend' | null;
   deviceId: string;
   deviceName: string;
   at: string;
}
export interface CoinsLiveResponse {
   coinsToday: number;
   coinCountToday: number;
   recentCoins: CoinRow[];
}
export interface CoinsListResponse {
   page: number;
   limit: number;
   total: number;
   coins: CoinRow[];
}

export const getMyCoinsLiveApi = async (): Promise<CoinsLiveResponse> => {
   const r = await apiClient.get('/vendo/coins/live');
   return r.data;
};

export const getMyCoinsApi = async (
   q: string,
   page: number
): Promise<CoinsListResponse> => {
   const params: Record<string, string> = { page: String(page) };
   if (q) params.q = q;
   const r = await apiClient.get('/vendo/coins', { params });
   return r.data;
};

/** One vendo with more unsold counter vouchers than a router keeps (the newest `limit`). */
export interface VoucherStockVendo {
   id: string;
   deviceId: string;
   name: string;
   parentName: string | null;
   unused: number;
   /** Unused codes beyond the newest `limit`: they work only while SmartFi is online. */
   onlineOnly: number;
   /** The vendo's router measured slow on its last user listing (10 s or more). */
   slow: boolean;
}

/** The home-page banner data: vendos over the window. Empty = no banner. */
export async function getVoucherStockWarningApi(): Promise<{ limit: number; vendos: VoucherStockVendo[] }> {
   const res = await apiClient.get('/vendo/vouchers/stock-warning');
   return res.data;
}

/** Delete a vendo's OLDEST unsold counter vouchers beyond the newest `keep` (sold or redeemed ones are never touched). */
export async function trimVendoVouchersApi(vendo: string, keep?: number): Promise<{ deleted: number }> {
   const res = await apiClient.post('/vendo/vouchers/trim', keep ? { vendo, keep } : { vendo });
   return res.data;
}

/** Website shield (#41): router-wide block / free-before-paying lists. */
export interface Shield {
   blockDomains: string[];
   freeDomains: string[];
   blockAdult: boolean;
}
export async function getShieldApi(
   deviceId: string
): Promise<{ shield: Shield; inheritedFrom: { id: string; name: string } | null }> {
   const r = await apiClient.get(`/vendo/devices/${deviceId}/shield`);
   return r.data;
}
export async function setShieldApi(
   deviceId: string,
   shield: Shield
): Promise<{ shield: Shield; applied: 'now' | 'next-checkin' }> {
   const r = await apiClient.put(`/vendo/devices/${deviceId}/shield`, shield);
   return r.data;
}

/* ── Antenna / radio IPs (Ray 2026-10-04) ───────────────────────────────── */

export interface ExtraAddressRow {
   _id: string;
   /** 'lan' = main customer LAN; 'vlan<id>' = a sub-vendo VLAN. */
   iface: string;
   address: string;
   mask: string;
   addedAt: string;
}

export async function listExtraAddressesApi(deviceId: string): Promise<{ extraAddresses: ExtraAddressRow[] }> {
   const r = await apiClient.get(`/vendo/devices/${deviceId}/extra-addresses`);
   return r.data;
}

export async function addExtraAddressApi(
   deviceId: string,
   body: { iface: string; address: string; mask: string }
): Promise<{ extraAddresses: ExtraAddressRow[]; warning: string | null; applied: boolean }> {
   const r = await apiClient.post(`/vendo/devices/${deviceId}/extra-addresses`, body);
   return r.data;
}

export async function removeExtraAddressApi(
   deviceId: string,
   entryId: string
): Promise<{ extraAddresses: ExtraAddressRow[] }> {
   const r = await apiClient.delete(`/vendo/devices/${deviceId}/extra-addresses/${entryId}`);
   return r.data;
}
